/**
 * The content registry: the single place game content is *declared as data*.
 *
 * Everything the game treats as "content" - the hues characters are painted
 * with, the land tiles a map is built from, the body-animation actions baked
 * for mobiles - lives here as plain, serializable definitions keyed by a stable
 * string id, looked up through a {@link Registry}. Both the renderer and the
 * offline converter *read* this registry; nothing here imports them.
 *
 * Quarried to walk-around: items, combat profiles, and monster templates were
 * left behind in `ultima` (see ADR-0001). The bake set was locomotion-only until
 * the Rat Swarm event (PRD #222) reintroduced a player attack clip and the
 * giant-rat body clips - that reversal is recorded in ADR-0116, which retires
 * the locomotion-only stance of ADR-0001.
 */

import { Direction } from './direction';

/** Common shape of every content definition: a stable, unique string id. */
export interface Definition {
  readonly id: string;
}

/**
 * A typed, immutable lookup of {@link Definition}s by id. Duplicate ids are
 * rejected at construction and unknown lookups throw, so content mistakes fail
 * loudly at startup rather than surfacing as a silent `undefined` deep in the
 * game. `kind` names the registry for those error messages.
 */
export class Registry<D extends Definition> {
  private readonly byId = new Map<string, D>();

  constructor(private readonly kind: string, defs: readonly D[]) {
    for (const def of defs) {
      if (this.byId.has(def.id)) {
        throw new Error(`duplicate ${kind} definition id: ${def.id}`);
      }
      this.byId.set(def.id, def);
    }
  }

  /** The definition for `id`, or throw if no such definition is declared. */
  get(id: string): D {
    const def = this.byId.get(id);
    if (!def) throw new Error(`unknown ${this.kind} definition id: ${id}`);
    return def;
  }

  /** Whether a definition with `id` is declared. */
  has(id: string): boolean {
    return this.byId.has(id);
  }

  /** Every declared definition, in declaration order. */
  all(): D[] {
    return [...this.byId.values()];
  }

  /** Every declared id, in declaration order. */
  get ids(): string[] {
    return [...this.byId.keys()];
  }
}

/** A named UO hue. `hue` is the 1-based hue id the runtime shader applies. */
export interface HueDefinition extends Definition {
  hue: number;
}

/**
 * A land tile. `id` is the friendly name a map's palette references; `artId` is
 * the UO land-art id the offline converter bakes a texture from.
 */
export interface TileDefinition extends Definition {
  artId: number;
}

/**
 * A static object. `id` is the friendly name a map's static placements
 * reference; `artId` is the raw UO static art id the converter bakes a texture
 * from (the archive lookup adds the 0x4000 offset).
 */
export interface StaticDefinition extends Definition {
  artId: number;
}

/**
 * The uniform upscale applied to **every** baked cursor raster (and its
 * hotspot). CSS cannot scale a `url()` cursor - there is no `cursor-size`, so a
 * "larger cursor" can only be a larger raster (#65). Both art paths nearest-
 * neighbour upscale the decoded/drawn tile by this factor (the real bake in
 * `tools/anim-converter/index.ts`, the clientless stub in `tools/gen-cursor.ts`)
 * and the runtime scales each hotspot by the same factor via {@link
 * scaledHotspot}, so the stub, the bake, and `src/render/gameCursor.ts` can never
 * drift. 1.3125x of the native 32x32 cursor tile is 42x42 - comfortably under the
 * browser ~128px cursor cap, below which an oversize cursor is silently dropped.
 */
export const CURSOR_SCALE = 1.3125;

/**
 * A mouse cursor. `id` is the friendly name used in the baked asset filename
 * (`assets/baked/cursor/<id>.webp`); `artId` is the raw UO **static** art id the
 * converter bakes the cursor image from (the archive lookup adds the 0x4000
 * offset, exactly like {@link StaticDefinition}). UO mouse cursors are ordinary
 * static art tiles - see the {@link cursors} registry for the id provenance.
 *
 * `hotspot` is the **native** (unscaled, source-tile pixel) fingertip click
 * point; the runtime scales it by {@link CURSOR_SCALE} to match the upscaled
 * raster. `dir` is the 8-way movement heading this cursor points while walking,
 * or `null` for the idle gauntlet shown when the player is not moving (#66) - the
 * runtime selects the cursor by this `Direction`.
 */
export interface CursorDefinition extends Definition {
  artId: number;
  readonly hotspot: { readonly x: number; readonly y: number };
  readonly dir: Direction | null;
}

/**
 * A native cursor hotspot mapped into the **upscaled** raster's pixel space by
 * {@link CURSOR_SCALE} (rounded to a whole pixel). One place both art paths and
 * the runtime derive the scaled fingertip from, so they never drift: the idle
 * gauntlet's native `(3,1)` becomes `(4,1)` at 1.3125x (round(3.9375), round(1.3125)).
 */
export function scaledHotspot(h: { readonly x: number; readonly y: number }): { x: number; y: number } {
  return { x: Math.round(h.x * CURSOR_SCALE), y: Math.round(h.y * CURSOR_SCALE) };
}

/**
 * A sound effect. `id` is the friendly name used in the baked asset filename
 * (`assets/baked/sound/<id>.wav`) and looked up at runtime; `file` is the source
 * `.wav` basename (no extension) in the UO sound pack that `npm run convert`
 * copies from (see the {@link sounds} registry for provenance). Sounds are
 * copyrighted UO material, so - like every other baked asset - the wavs are
 * gitignored and regenerated locally: the real converter copies them and the
 * clientless stub writes a silent placeholder per id, so the two never drift.
 */
export interface SoundDefinition extends Definition {
  file: string;
}

/**
 * A background-music track. `id` is the friendly name used in the baked asset
 * filename (`assets/baked/music/<id>.mid`) and looked up at runtime; `file` is the
 * source `.mid` basename (no extension) in the UO music pack that `npm run convert`
 * copies from (see the {@link music} registry for provenance). Like the sound
 * effects and every other baked asset, the UO `.mid` files are copyrighted material,
 * so they are gitignored and regenerated locally: the real converter copies them
 * (`copyMusic`) and the clientless stub writes a tiny silent placeholder per id
 * (`stubMusic`), so the two never drift and a host without the pack still builds and
 * runs (the {@link ../audio/musicPlayer.MusicPlayer} degrades to silence, exactly as
 * the sound pipeline does). ADR-0146.
 */
export interface MusicDefinition extends Definition {
  file: string;
}

/**
 * A UO **effect animation** - a directionless one-shot/looping FX strip (a fireball,
 * an explosion) whose frames are ordinary **static art tiles** chained together by
 * the client's `AnimData.mul` table, not `anim.mul` body clips. `id` is the friendly
 * name used in the baked asset filename (`assets/baked/effects/<id>.{png,json}`) and
 * looked up at runtime; `artId` is the raw UO static art id of the effect's *first*
 * frame - the `AnimData.mul` entry at that id lists the remaining frames as signed
 * offsets from it (see `tools/anim-converter/animDataMul.ts`). Both the real
 * converter (`bakeEffects`) and the clientless stub (`stubEffects`) walk this
 * registry, so the baked effect set can never drift from what the runtime globs
 * (`src/render/effectsArt.ts`). ADR-0180.
 */
export interface EffectDefinition extends Definition {
  artId: number;
  /**
   * An explicitly authored static-art sequence for effects that ClassicUO
   * advances itself instead of reading from AnimData.mul.
   * Lightning is the canonical case: ten consecutive frames beginning at
   * 0x4E20, stepped every 40ms by LightningEffect.
   */
  frameArtIds?: readonly number[];
  /** Frame pacing for {@link frameArtIds}. */
  frameIntervalMs?: number;
  /**
   * Read {@link frameArtIds} from gumpartLegacyMUL.uop instead of static art.
   * ClassicUO draws LightningEffect through its gump loader.
   */
  gumpArt?: boolean;
  /** World placement for frames that do not carry an authored animation anchor. */
  anchor?: 'center' | 'bottom';
  /** Render through Pixi's additive blend mode, matching ClassicUO's effect view. */
  additive?: boolean;
  /**
   * A single-frame **item-art** effect (archer, PRD #446 Slice 1, ADR-0237): the
   * flying arrow / crossbow bolt are plain UO *item* statics (0x0F42 / 0x1BFE),
   * NOT `AnimData.mul`-chained strips - so the bake reads one static tile (the
   * {@link ../../tools/anim-converter/index.bakeStatics} 1-frame decode) instead
   * of `readAnimDataEntry` (which throws on a non-animated tile). The runtime
   * rotates the one frame client-side along the flight vector. Absent/false =
   * an ordinary AnimData strip (every existing effect).
   */
  itemArt?: boolean;
}

/**
 * A body-animation action. `id` is the friendly action name (used in baked
 * asset filenames); `action` is the UO anim group number the converter decodes.
 *
 * The group number is only meaningful **within a body class**: UO's anim.mul
 * lays out three different action tables (monster/"high detail" for bodies
 * < 200, animal/"low detail" for 200..399, people for 400+), and the same
 * action id means a different clip in each. So actions live in per-class
 * registries ({@link actions} for people, {@link animalActions} for animals)
 * and {@link actionsForBody} selects the right table per body (ADR-0116).
 */
export interface ActionDefinition extends Definition {
  action: number;
}

/**
 * The UO animation-group class a body's actions index into. `people` is the
 * human table (bodies 400+, walk 0 / run 2 / stand 4 / fidget 5 / combat clips);
 * `animal` is the low-detail table (bodies 200..399, walk 0 / stand 2 / die 8).
 * `monster` is the high-detail table (bodies < 200, walk 0 / stand 1 / die 2 /
 * attacks 4/6/7 / stomp 12), first used by the Blackthorn's Storm energy vortex
 * (PRD #289 Slice B, ADR-0148); see {@link monsterActions}.
 *
 * `ghost` is NOT a distinct UO anim-group table - the ghost bodies (0x192/0x193)
 * sit in the same people index band and share the people strides - but a
 * deliberately **restricted bake set** ({@link ghostActions}: stand only). A UO human
 * ghost has no art of its own at all (the client maps it to the living body by
 * subtracting 2; ADR-0142), so the ghost bake decodes zero frames and is now
 * **non-fatally skipped** rather than aborting `npm run convert` (unlike a confirmed
 * body). This registry is unused at render time - the renderer draws a dead duelist
 * off the living body as the death shroud - but is kept as documentation (ADR-0129).
 *
 * `blackthorn` is the same idea for a people-band NPC: Lord Blackthorn (body 991)
 * sits in the people index band but bakes a **restricted set** ({@link
 * blackthornActions}: walk / stand / summon-cast) rather than the full player people
 * table, so his fatal bake never demands a player-only combat/duel clip he lacks
 * (PRD #289 Slice B, ADR-0148).
 *
 * `llama` is the {@link LLAMA_BODY chaos-llama} storm variant (ADR-0162): a
 * low-detail **animal**-band body (0xDC = 220), so it uses the animal anim-group
 * indices - but it reuses the whole vortex pursuit brain, so it must bake the same
 * clip *ids* a vortex broadcasts (walk / stand / attack-a/b/c / materialize / die),
 * mapped to the animal table's indices ({@link llamaActions}), not the vortex's
 * monster table. A restricted named set like {@link blackthornActions}, so its fatal
 * body bake asks only for clips the llama art has.
 */
export type BodyActionSet =
  | 'people'
  | 'animal'
  | 'mount'
  | 'mount-monster'
  | 'single-pose'
  | 'card-animal'
  | 'card-monster'
  | 'ghost'
  | 'monster'
  | 'bestiary'
  | 'bestiary-two-attack'
  | 'blackthorn'
  | 'llama';

/**
 * A character body the converter bakes animation clips for. `id` is the friendly
 * content name; `body` is the UO body id.
 *
 * `actionSet` names the animation-group table the body's clips index into
 * (default `people`; see {@link BodyActionSet}) - a body in the wrong table
 * decodes garbage or nothing, because the three tables assign the same action
 * number to different clips.
 *
 * `playable` marks whether the splash may offer the body as a player character
 * (default `true`). Event creatures like the giant rat are baked and rendered
 * from this same registry but are `playable: false`, so login validation
 * (`server/identity.ts`) never accepts them as a `sex` - a hostile login cannot
 * spawn a rat-bodied player.
 *
 * `baseGraphic` is the UO anim graphic the converter actually **decodes**, when the
 * client's `bodyconv` table remaps this body id to a different base graphic that
 * carries the art (default: `body` itself, no remap). The energy vortex (164) is the
 * first such body - its frames live on the air-elemental base graphic 13 (ADR-0148).
 * The baked file/manifest still key off the registry `body` id, so the renderer looks
 * the clips up by the body it sees on the wire; only the decode source differs.
 *
 * `hue` is a fixed UO hue baked **into the manifest** for the renderer to apply, for
 * a body that is always painted one colour (the energy vortex's hue 20) rather than a
 * per-connection player hue (default: none). Recorded so a later render slice can tint
 * the monster without a second lookup (ADR-0148).
 */
export interface BodyDefinition extends Definition {
  body: number;
  actionSet?: BodyActionSet;
  /**
   * Whether this body may be drawn beneath a rider.
   *
   * This is independent from `actionSet` because one body may serve more than one
   * gameplay role. The llama keeps its storm animation set while also exposing the
   * ordinary animal gait clips required by the mount renderer.
   */
  mountable?: boolean;
  /**
   * How this body composes with the mounted character.
   *
   * The default `under-rider` keeps the normal seated body and equipment stack above
   * the mount. `replacement` draws the mount alone once its art has loaded.
   */
  mountPresentation?: 'under-rider' | 'replacement';
  /** Visual scale applied only when this body is drawn as a mount (default 1). */
  mountScale?: number;
  playable?: boolean;
  baseGraphic?: number;
  hue?: number;
  /**
   * Where this body's baked art comes from (default `client`: decoded out of the
   * local UO installation's `anim.mul`/`anim*.uop` by `npm run convert`).
   *
   * `import` marks a body whose frames the UO client does not contain at all, and
   * which is instead baked from an external sprite sheet by `npm run import-mobile`
   * ({@link ../../tools/sprite-import/README.md}). The camel mount is the first
   * (ADR-0442): a full scan of the captain's client - every body id 1..2200 across
   * `anim.mul`, the `Bodyconv.def` expansion files, the `AnimationFrame*.uop`
   * archives, and the `Body.def` aliases - decodes no camel, so there is nothing for
   * the converter to read.
   *
   * The distinction is load-bearing for the bake plan: {@link
   * ../../tools/anim-converter/bakePlan.mobileBakePlan} skips imported bodies, so
   * `npm run convert` never reports a spurious empty-decode for art it was never
   * meant to own, while `npm run stub-assets` still stubs them like any other body.
   */
  source?: 'client' | 'import';
  /**
   * For a `mount` body: how many pixels to raise the **rider** so they sit on this
   * animal's back instead of inside its ribs (ADR-0442). Default 0.
   *
   * UO's seated rider art (people groups 23-25) is authored against the height of a UO
   * horse's back, and its per-frame anchors bake that assumption in. Any mount with a
   * different build needs the difference made up, and it is a property of the animal
   * rather than of the rider - a camel is taller and humped, so its rider sits higher
   * than a horse's would. Declared per body so a second mount is still one registry row.
   */
  riderOffsetY?: number | readonly number[];
  /**
   * Optional absolute rider lift used while the mount is resting.
   *
   * Some bodies register their standing back lower than their moving gait even
   * though the moving rider is already seated correctly. When absent, resting uses
   * `riderOffsetY`.
   */
  riderIdleOffsetY?: number | readonly number[];
}

/**
 * The UO body id of the Rat Swarm event's giant rat (PRD #222, ADR-0116):
 * RunUO/ServUO's `GiantRat` uses body `0xD7` = 215, in the animal (low-detail)
 * animation band. Declared here, beside the {@link bodies} registry that bakes
 * it, so the rat-entity slice (Slice C) and the renderer reference the same id
 * the converter bakes. VERIFY: the id and its clips are confirmed against real
 * client data when the captain runs `npm run convert` (no UO client exists on
 * the dev/CI hosts; the stub pipeline covers them until then).
 */
export const GIANT_RAT_BODY = 0xd7;

/**
 * The UO body ids of the two **ghost** bodies a duel death swaps a defeated player
 * onto (PRD #252 Duel slice B, ADR-0129): the male ghost `0x192` = 402 and the
 * female ghost `0x193` = 403, the classic UO player-ghost bodies. They are the
 * replicated "this player is dead" marker (`renderable.body` swapped to one of these,
 * reusing the existing body-id field with no new flag), which the renderer keys the
 * whole death-shroud look off (`isGhostBody`).
 *
 * **Confirmed vs ClassicUO (scout study, ADR-0142):** a UO human ghost has **no
 * animation art of its own** - the client maps the ghost graphic to the LIVING body
 * by subtracting 2 (`0x192 -> 0x190` = 400, `0x193 -> 0x191` = 401). So the renderer
 * draws a ghost body off its correct-sex living frames ({@link
 * ../render/playerArt.loadBodyFrames}); the "zero frames" the converter finds for
 * these ids is expected, not a bug. The dead look itself is the ClassicUO death
 * shroud - a **solid, grayscale, robed** figure over the frozen die pose ({@link
 * SHROUD_ID}, {@link ../render/buildPlayerLayers}) - not translucency.
 */
export const GHOST_BODY_MALE = 0x192;
export const GHOST_BODY_FEMALE = 0x193;

/**
 * Blackthorn's Storm event bodies (PRD #289 Slice B, ADR-0148), both baked here and
 * both `playable: false` (rejected at login exactly like the giant rat).
 *
 * The **energy vortex** is UO body `0xA4` = 164, in the high-detail *monster* band
 * (bodies < 200) - the codebase's first monster-band body. Its own graphic carries no
 * art: the client's `bodyconv` table remaps it to base graphic `0xD` = 13 (the air
 * elemental) and paints it {@link ENERGY_VORTEX_HUE hue 20}, so the converter decodes
 * graphic 13 (see {@link BodyDefinition.baseGraphic}) and records the hue in the
 * manifest. **VERIFY** the body id, the 164->13 remap, and hue 20 against real client
 * data at the captain's `npm run convert` (no UO client on dev/CI; the stub covers them).
 *
 * **Lord Blackthorn** is UO body `0x3DF` = 991, a people-band (400+) human body with
 * no remap (base graphic 991, hue 0). He bakes a restricted {@link blackthornActions}
 * set (walk / stand / summon-cast). **VERIFY** the body id at bake.
 */
export const ENERGY_VORTEX_BODY = 0xa4;
/** The air-elemental base graphic the vortex body (164) bodyconv-remaps to. VERIFY at bake. */
export const ENERGY_VORTEX_BASE_GRAPHIC = 0xd;
/** The fixed UO hue the vortex art is painted with, recorded in the manifest. VERIFY at bake. */
export const ENERGY_VORTEX_HUE = 20;
/** Lord Blackthorn's UO body id (0x3DF = 991, people band, no remap). VERIFY at bake. */
export const BLACKTHORN_BODY = 0x3df;

/**
 * The **camel** mount's body id (`0xEB` = 235, ADR-0442) - britbank's first ridable
 * mount, and its first {@link BodyDefinition.source `import`} body.
 *
 * The id is chosen, not inherited. The captain's UO client contains no camel at all
 * (a full 1..2200 body scan across `anim.mul`, the `Bodyconv.def` expansion files, the
 * `AnimationFrame*.uop` archives, and the `Body.def` aliases decodes horses, llamas,
 * ostards, cows, bulls, goats, sheep and bears - no camel), so unlike every other body
 * here this number does not name existing UO art. 235 is a **free** slot in the
 * low-detail animal band (200..399) that no client graphic and no bestiary row claims,
 * which keeps the camel's {@link mountActions} animal-table indices honest and keeps it
 * inside the band the login gate and monster predicates already reason about.
 *
 * Because the art is imported rather than decoded, this id only ever has to be unique
 * on the wire and stable across bakes - `src/sim/content.test.ts` pins both.
 */
export const CAMEL_BODY = 0xeb;

/**
 * Stable wire id for the imported lowrider.
 *
 * Custom imported bodies use the reserved high body-id band so they cannot collide
 * with art decoded from a UO client.
 */
export const LOWRIDER_BODY = 0x7f00;

/** Admin-only cat mount body, remapped by the client to black-cat graphic 0xD6. */
export const CAT_MOUNT_BODY = 0x40;

/** Admin-only Terathan Queen mount body, called Matriarch in the shared bestiary. */
export const TERATHAN_QUEEN_MOUNT_BODY = 0x48;

/** Admin-only mountain-goat mount body, remapped by the client to graphic 0xD1. */
export const MOUNTAIN_GOAT_MOUNT_BODY = 0x58;

export const SAINT_BERNARD_MOUNT_BODY = 0x60d;
export const WOLF_MOUNT_BODY = 0x60b;
export const POODLE_MOUNT_BODY = 0x60f;
export const DOBERMAN_MOUNT_BODY = 0x610;
export const CAPYBARA_MOUNT_BODY = 0x5f7;
export const WEREWOLF_MOUNT_BODY = 0x582;

/**
 * How far to raise a rider so they sit on the camel's back, **per facing** (ADR-0442),
 * in {@link ../sim/direction.Direction} order: N, NE, E, SE, S, SW, W, NW.
 *
 * UO's seated art is authored against the height of a horse's back, and a camel is both
 * taller and humped, so an unadjusted rider sinks into its ribs with their legs hanging
 * through its belly. The correction is per facing rather than one number because in a
 * 2:1 dimetric projection an animal's back meets the viewer at a different screen height
 * from each angle - a head-on camel is foreshortened and its back reads higher, a
 * side-on one is not - so a single lift that seats the rider correctly in profile leaves
 * them floating or sunk on the diagonals.
 *
 * Derived by compositing the baked rider stand frame over the baked camel frame in all
 * eight facings and aligning the rider's seat to the camel's back contour. This is the
 * one knob to nudge if a facing ever looks off: raise a value to lift the rider.
 */
export const CAMEL_RIDER_OFFSET_Y = 37;

/**
 * The **chaos llama** energy-vortex variant's UO body id (`0xDC` = 220, the classic UO
 * llama, ADR-0162). On each storm summon there is a {@link ../sim/vortex.LLAMA_SPAWN_CHANCE 20%}
 * chance a vortex spawns as a llama instead - a reskinned, buffed hazard (purple hue,
 * one-hit kill, double speed) that otherwise reuses the whole vortex pursuit/lifecycle. It
 * is a monster-band-adjacent creature body baked like any other; **VERIFY** the body id at
 * the captain's `npm run convert`.
 */
export const LLAMA_BODY = 0xdc;

/**
 * The fixed UO hue a chaos-llama vortex is painted. Since ADR-0170 this is defined as the
 * **same {@link ENERGY_VORTEX_HUE energy-vortex hue}** so a chaos llama renders the identical
 * purple as a normal energy vortex (the captain's request - they must not drift apart). It
 * rides `renderable.bodyHue` exactly as the vortex's own hue does. Kept as its own named
 * constant so the llama spawn path (and its test) reads intent, but aliased to
 * {@link ENERGY_VORTEX_HUE} so there is a single source of truth for the storm purple.
 * (Originally hue 1165, ADR-0162; realigned to the EV hue in ADR-0170.)
 */
export const LLAMA_HUE = ENERGY_VORTEX_HUE;

/** The living female player body id; a ghost swap keys the female ghost off it (400 male, 401 female). */
const FEMALE_BODY = 401;

/**
 * The correct-sex ghost body a defeated `livingBody` swaps onto (PRD #252 Duel
 * slice E, ADR-0136): the female body (401) becomes the female ghost, everything
 * else the male ghost (the male body 400 is the sole other player body, and an
 * unexpected id degrades to the male ghost rather than throwing). The single seam
 * the death-logic slice maps through, so the server's death swap and the
 * resurrection swap-back agree on the same living<->ghost pairing.
 */
export function ghostBodyForLiving(livingBody: number): number {
  return livingBody === FEMALE_BODY ? GHOST_BODY_FEMALE : GHOST_BODY_MALE;
}

/** Whether a UO body id is one of the two duel ghost bodies (a downed duelist). */
export function isGhostBody(body: number | undefined): boolean {
  return body === GHOST_BODY_MALE || body === GHOST_BODY_FEMALE;
}

/**
 * The rider's three **mounted** locomotion clips (ADR-0442), in the order the render
 * path substitutes them for the ordinary gait: a mounted rider's `walk` is the
 * seated {@link actions `ride-slow`}, their `run` is `ride-fast`, and their `stand`
 * is `ride-stand`.
 *
 * Exported as one list because three separate consumers must agree on it exactly:
 * {@link ../render/buildPlayerLayers.mountedClips} does the substitution, the lazy
 * atlas store streams these three clips on first sight of a mounted look (the
 * ARMED_LOCOMOTION_CLIP_IDS precedent - they are outside the eager locomotion
 * bundle), and the placeholder-degrade path treats them as optional so an unbaked
 * host shows an ordinary standing rider rather than a stub box.
 */
export const RIDE_CLIP_IDS: readonly string[] = ['ride-slow', 'ride-fast', 'ride-stand'];

/**
 * The set of UO body ids that are **mounts** - bodies drawn *under* a rider rather
 * than as a mobile of their own. Derived from the {@link bodies} registry's explicit
 * `mountable` capability rather than a hardcoded list or animation role, so a body
 * such as the llama may remain a storm creature while also serving as a mount.
 *
 * Memoized once ({@link isHumanBody PLAYABLE_BODIES} precedent) for the O(1)
 * predicate below. Declared after the registry it reads, like the other derived sets.
 */
let MOUNT_BODIES: ReadonlySet<number> | undefined;

/**
 * Whether a UO body id names a mount. The gate the server's admin mount power uses to
 * reject a `renderable.mount` that is not actually a mount body, so a hostile or
 * stale client can never ask the renderer to draw an arbitrary creature - a dragon, a
 * second player - underneath a rider.
 */
export function isMountBody(body: number | undefined): boolean {
  MOUNT_BODIES ??= new Set(bodies.all().filter((b) => b.mountable).map((b) => b.body));
  return body !== undefined && MOUNT_BODIES.has(body);
}

/**
 * Whether a UO body id is one of Blackthorn's Storm event bodies - the energy
 * vortex (164) or Lord Blackthorn (991) (PRD #289 Slice E, ADR-0152). Unlike the
 * giant rat (a war-mode *target* highlighted grey) these read as **scenery**: the
 * renderer wires neither the rat war-target nor the duel-target hover/click onto
 * a storm-body sprite, so hovering a vortex or Blackthorn in either mode does
 * nothing (they are unkillable and untargetable). The client identifies them by
 * the UO body id already on the renderable, the same way it spots a rat by {@link
 * GIANT_RAT_BODY} - no new snapshot field. The {@link LLAMA_BODY chaos-llama} vortex
 * variant (ADR-0162) is a storm body too, so it reads as scenery exactly like a normal
 * vortex (never a duel target, never a war target).
 */
export function isStormBody(body: number | undefined): boolean {
  return body === ENERGY_VORTEX_BODY || body === BLACKTHORN_BODY || body === LLAMA_BODY;
}

/**
 * Whether a UO body id is one of the storm's two **hunting-hazard** creatures - the
 * energy vortex ({@link ENERGY_VORTEX_BODY}) or its {@link LLAMA_BODY chaos-llama}
 * variant (ADR-0162). Both share the {@link ../sim/vortex} pursuit brain and the
 * client's wind/attack cues, so this is the discriminator the audio layer keys off to
 * decide "this mobile is a roaming vortex-kind" (distinct from {@link isStormBody},
 * which also covers Lord Blackthorn, who casts rather than hunts).
 */
export function isVortexBody(body: number | undefined): boolean {
  return body === ENERGY_VORTEX_BODY || body === LLAMA_BODY;
}

/**
 * Whether a UO body id is the {@link LLAMA_BODY chaos-llama} vortex variant (ADR-0162).
 * The one discriminator the sim (instakill) and the client (purple hue, llama sounds)
 * both read off the replicated body id to tell a llama from a normal energy vortex - no
 * new snapshot field, the {@link isGhostBody}/{@link isStormBody} precedent.
 */
export function isLlamaBody(body: number | undefined): boolean {
  return body === LLAMA_BODY;
}

// ---------------------------------------------------------------------------
// The monster bestiary (arena phase-1 roster, ADR-0184) - a REUSABLE registry
// of classic UO creatures for the minigames. The arena is the first consumer
// (its tier list draws from these ids); future modes reuse the same bestiary
// rather than registering their own bodies. Every entry is content-as-data:
// the display name the HUD/heralds can float, and the UO body id the renderer
// draws. All bake automatically: {@link bodies} spreads the bestiary in with
// `actionSet: 'bestiary'` (walk 0 / stand 1 / die 2 / attack-a/-b/-c 4/5/6 - the
// minimal high-detail group every creature populates, so the horde's swing clips
// exist for every monster), `playable: false` (login-rejected), no remap, natural
// hue. The vortex's richer {@link monsterActions} (with AttackCrossbow 7 / Stomp 12)
// stays its own; simple creatures like the slime have no 7 or 12 (see
// {@link bestiaryActions}).
//
// **Every body id is VERIFY** (no UO client on dev/CI): the captain's
// `npm run convert` decodes each one fatally-loud, which is the intended
// check. The two 200-band entries (darknight creeper 313, demon knight 318)
// additionally VERIFY that bodyconv/anim expansion files carry them with the
// high-detail (monster) action layout - a wrong band decodes garbage frames
// loudly at bake, never silently in game.
// ---------------------------------------------------------------------------

/** The seven weapon, spellbook, and instrument super-slayer groups used by UO creatures. */
export const SLAYER_GROUPS = [
  'arachnid',
  'demon',
  'elemental',
  'fey',
  'repond',
  'reptile',
  'undead',
] as const;
export type SlayerGroup = (typeof SLAYER_GROUPS)[number];

/** Creature-specific weapon, spellbook, and instrument slayers represented in the bestiary. */
export type LesserSlayer =
  | 'air-elemental'
  | 'blood-elemental'
  | 'dragon'
  | 'earth-elemental'
  | 'fire-elemental'
  | 'gargoyle'
  | 'lizardman'
  | 'ogre'
  | 'ophidian'
  | 'orc'
  | 'poison-elemental'
  | 'scorpion'
  | 'snake'
  | 'snow-elemental'
  | 'spider'
  | 'terathan'
  | 'troll'
  | 'water-elemental';

/** Primarily talisman-based slayers that overlap the seven primary groups. */
export type TalismanSlayer =
  | 'bat'
  | 'bear'
  | 'bird'
  | 'flame'
  | 'ice'
  | 'mage'
  | 'vermin'
  | 'wolf';

/** One bestiary creature: its shared display name, Slayer classification, and canonical UO appearance. */
export interface MonsterDefinition extends Definition {
  /** Display name (boss name floats, heralds, future bestiary UI). */
  name: string;
  /** UO body id (VERIFY at bake). */
  body: number;
  /**
   * Canonical encoded UO hue.
   * Omit for the body's authored colours.
   * The 0x8000 partial-hue and 0x4000 spectral flags are preserved for the renderer.
   */
  hue?: number;
  /** This identity belongs to cards but is deliberately absent from Arena content. */
  cardOnly?: boolean;
  /** Anim graphic that actually carries this body's art after a client remap. */
  baseGraphic?: number;
  /** Storage layout used by a card-only body's idle animation. */
  animationSet?: 'animal' | 'monster';
  /** Whether this bestiary body may also be drawn beneath an admin rider. */
  mountable?: boolean;
  /** Arena creature sound family; card-only identities do not need world audio. */
  soundFamily?: MonsterSoundFamily;
  /**
   * Primary UO super-slayer group.
   * Null is an explicit audited classification, not a missing default.
   */
  slayerGroup: SlayerGroup | null;
  /** Creature-specific Slayer properties that also affect this monster. */
  lesserSlayers?: readonly LesserSlayer[];
  /** Talisman Slayer properties that also affect this monster. */
  talismanSlayers?: readonly TalismanSlayer[];
}

/** The elite Demon Knight's UO body id (0x13E = 318): progressive-elites #12. VERIFY at bake. */
export const DEMON_KNIGHT_BODY = 0x13e;

/**
 * The arena boss's dedicated UO body id (0x2E5 = 741): the climax Demon Knight,
 * deliberately distinct from the elite Demon Knight (0x13E) so the boss and elite
 * #12 never share a body (keeps {@link isArenaBossBody} unambiguous). VERIFY at bake.
 */
export const DEMON_KNIGHT_BOSS_BODY = 0x2e5;

/** The former arena elite's UO body id: the Darknight Creeper (0x139 = 313). VERIFY at bake. */
export const DARKNIGHT_CREEPER_BODY = 0x139;

/**
 * The arena elite's UO body id: the Abysmal Horror (0x138 = 312, ADR-0217, replacing
 * the Darknight Creeper). Like the creeper it is a 200-band body decoded on the
 * high-detail (monster) action layout - its group indices (walk 0 / idle 1 / die 2 /
 * attacks 4-6, the {@link bestiaryActions} it bakes) are ServUO's AbysmalHorror. VERIFY at bake.
 */
export const ABYSMAL_HORROR_BODY = 0x138;

// The progressive-elites mini-boss band (0x12F-0x13F): 200-band bodies decoded on
// the high-detail (monster) action layout, like the elite/boss above. VERIFY at bake.

/** Progressive-elites mini-boss: the Gibberling (0x133 = 307). VERIFY at bake. */
export const GIBBERLING_BODY = 0x133;

/** Progressive-elites mini-boss: the Mound of Maggots (0x13F = 319). VERIFY at bake. */
export const MOUND_OF_MAGGOTS_BODY = 0x13f;

/** Progressive-elites mini-boss: the Patchwork Skeleton (0x135 = 309). VERIFY at bake. */
export const PATCHWORK_SKELETON_BODY = 0x135;

/** Progressive-elites mini-boss: the Bone Demon (0x134 = 308). VERIFY at bake. */
export const BONE_DEMON_BODY = 0x134;

/** Progressive-elites mini-boss: the Gore Fiend (0x131 = 305). VERIFY at bake. */
export const GORE_FIEND_BODY = 0x131;

/** Progressive-elites mini-boss: the Devourer of Souls (0x12F = 303). VERIFY at bake. */
export const DEVOURER_BODY = 0x12f;

/** Progressive-elites mini-boss: the Flesh Golem (0x130 = 304). VERIFY at bake. */
export const FLESH_GOLEM_BODY = 0x130;

/** Progressive-elites mini-boss: the Impaler (0x132 = 306). VERIFY at bake. */
export const IMPALER_BODY = 0x132;

/** Progressive-elites mini-boss: the Wanderer of the Void (0x13C = 316). VERIFY at bake. */
export const WANDERER_BODY = 0x13c;

/**
 * The bestiary (ADR-0184, expanded ADR-0211): the reusable classic-UO monster
 * registry every minigame draws from. Grouped by theme; the arena's tier tables
 * ({@link ../sim/arena.ARENA_STAGES}) pick which creatures appear when. Body ids
 * and BaseSoundIDs are ported from ServUO (github.com/ServUO) and are **VERIFY**
 * at the captain's `npm run convert` (a wrong body id is a fatal decode there).
 * The dedicated arena bosses (balron..semidar) sit at the end with the elite;
 * their bodies are deliberately absent from the wave tiers so the client's
 * {@link isArenaBossBody} set-check can't misfire on a wave spawn.
 */
export const monsters = new Registry<MonsterDefinition>('monster', [
  // Critters & beasts.
  { id: 'slime', name: 'Slime', body: 0x33, hue: 2212, soundFamily: 'slime', slayerGroup: null }, // Stable green from ServUO's 2201-2224 range.
  { id: 'snake', name: 'Snake', body: 0x34, hue: 2009, soundFamily: 'snake', slayerGroup: 'reptile', lesserSlayers: ['snake'] }, // Stable gold from ServUO's 2001-2018 range.
  { id: 'mongbat', name: 'Mongbat', body: 0x27, soundFamily: 'mongbat', slayerGroup: null, talismanSlayers: ['bat'] }, // 39. VERIFY
  { id: 'giant-spider', name: 'Giant Spider', body: 0x1c, soundFamily: 'spider', slayerGroup: 'arachnid', lesserSlayers: ['spider'] }, // 28. VERIFY
  { id: 'dread-spider', name: 'Dread Spider', body: 0xb, hue: 1401, soundFamily: 'spider', slayerGroup: 'arachnid', lesserSlayers: ['spider'] }, // Body.def 11 -> 28, hue 1401.
  { id: 'scorpion', name: 'Scorpion', body: 0x30, soundFamily: 'scorpion', slayerGroup: 'arachnid', lesserSlayers: ['scorpion'] }, // 48. VERIFY
  { id: 'dire-wolf', name: 'Dire Wolf', body: 0x17, hue: 2305, soundFamily: 'wolf', slayerGroup: null, talismanSlayers: ['wolf'] }, // Body.def 23 -> 225, hue 2305.
  { id: 'hell-hound', name: 'Hell Hound', body: 0x62, hue: 1643, soundFamily: 'wolf', slayerGroup: null, talismanSlayers: ['flame'] }, // Body.def 98 -> 225, hue 1643.
  { id: 'harpy', name: 'Harpy', body: 0x1e, soundFamily: 'harpy', slayerGroup: null }, // 30. VERIFY
  { id: 'stone-harpy', name: 'Stone Harpy', body: 0x49, hue: 1402, soundFamily: 'harpy', slayerGroup: null }, // Body.def 73 -> 30, hue 1402.
  { id: 'imp', name: 'Imp', body: 0x4a, hue: 1652, soundFamily: 'mongbat', slayerGroup: 'demon' }, // Body.def 74 -> 39, hue 1652.
  { id: 'corpser', name: 'Corpser', body: 0x8, soundFamily: 'corpser', slayerGroup: null }, // 8. VERIFY
  { id: 'reaper', name: 'Reaper', body: 0x2f, soundFamily: 'reaper', slayerGroup: null }, // 47. VERIFY
  // The restless dead.
  { id: 'spirit', name: 'Spirit', body: 0x1a, hue: 0x4001, soundFamily: 'shadows', slayerGroup: 'undead' }, // ServUO Shade/Spectre/Wraith spectral hue.
  { id: 'zombie', name: 'Zombie', body: 0x3, soundFamily: 'zombie', slayerGroup: 'undead' }, // 3. VERIFY
  { id: 'skeleton', name: 'Skeleton', body: 0x32, soundFamily: 'skeleton', slayerGroup: 'undead' }, // 50. VERIFY
  { id: 'skeleton-warrior', name: 'Skeleton Warrior', body: 0x39, soundFamily: 'skeleton', slayerGroup: 'undead' }, // 57. VERIFY
  { id: 'ghoul', name: 'Ghoul', body: 0x99, soundFamily: 'shadows', slayerGroup: 'undead' }, // 153. VERIFY (BaseSoundID 1154 = shadows)
  { id: 'skeletal-knight', name: 'Skeletal Knight', body: 0x93, soundFamily: 'skeleton', slayerGroup: 'undead' }, // 147. VERIFY (451 = skeleton)
  { id: 'bone-magi', name: 'Bone Magi', body: 0x94, hue: 1625, soundFamily: 'skeleton', slayerGroup: 'undead' }, // Body.def 148 -> 50, hue 1625.
  { id: 'rotting-corpse', name: 'Rotting Corpse', body: 0x9b, hue: 2003, soundFamily: 'zombie', slayerGroup: 'undead' }, // Body.def 155 -> 3, hue 2003.
  { id: 'mummy', name: 'Mummy', body: 0x9a, hue: 1402, soundFamily: 'zombie', slayerGroup: 'undead' }, // Body.def 154 -> 3, hue 1402.
  // The warbands.
  { id: 'orc', name: 'Orc', body: 0x11, soundFamily: 'orc', slayerGroup: 'repond', lesserSlayers: ['orc'] }, // 17. VERIFY
  { id: 'orc-captain', name: 'Orc Captain', body: 0x7, soundFamily: 'orc', slayerGroup: 'repond', lesserSlayers: ['orc'] }, // 7. VERIFY
  { id: 'orc-warrior', name: 'Orc Warrior', body: 0x29, soundFamily: 'orc', slayerGroup: 'repond', lesserSlayers: ['orc'] }, // 41. VERIFY
  { id: 'lizardman', name: 'Lizardman', body: 0x23, soundFamily: 'lizardman', slayerGroup: 'reptile', lesserSlayers: ['lizardman'] }, // 35. VERIFY
  { id: 'ratman', name: 'Ratman', body: 0x2a, soundFamily: 'ratman', slayerGroup: 'repond', talismanSlayers: ['vermin'] }, // 42. VERIFY
  { id: 'ratman-archer', name: 'Ratman Archer', body: 0x8e, hue: 0x806b, soundFamily: 'ratman', slayerGroup: 'repond', talismanSlayers: ['vermin'] }, // Body.def partial hue 107.
  { id: 'ratman-mage', name: 'Ratman Mage', body: 0x8f, hue: 0x8020, soundFamily: 'ratman', slayerGroup: 'repond', talismanSlayers: ['vermin'] }, // Body.def partial hue 32.
  { id: 'ettin', name: 'Ettin', body: 0x12, soundFamily: 'ettin', slayerGroup: 'repond' }, // 18. VERIFY
  // The brutes.
  { id: 'ogre', name: 'Ogre', body: 0x1, soundFamily: 'ogre', slayerGroup: 'repond', lesserSlayers: ['ogre'] }, // 1. VERIFY (427 = ogre)
  { id: 'ogre-lord', name: 'Ogre Lord', body: 0x53, soundFamily: 'ogre', slayerGroup: 'repond', lesserSlayers: ['ogre'] }, // 83. VERIFY (427 = ogre)
  { id: 'troll', name: 'Troll', body: 0x35, soundFamily: 'troll', slayerGroup: 'repond', lesserSlayers: ['troll'] }, // 53. VERIFY (ServUO randomises 53/54; we pin 53)
  { id: 'gazer', name: 'Gazer', body: 0x16, soundFamily: 'gazer', slayerGroup: null }, // 22. VERIFY
  { id: 'headless-one', name: 'Headless One', body: 0x1f, hue: 1030, soundFamily: 'headless', slayerGroup: null }, // Stable midpoint of ServUO's skin-hue range.
  { id: 'gargoyle', name: 'Gargoyle', body: 0x4, soundFamily: 'gargoyle', slayerGroup: 'demon', lesserSlayers: ['gargoyle'] }, // 4. VERIFY
  { id: 'stone-gargoyle', name: 'Stone Gargoyle', body: 0x43, hue: 1401, soundFamily: 'gargoyle', slayerGroup: 'demon', lesserSlayers: ['gargoyle'] }, // Body.def 67 -> 4, hue 1401.
  { id: 'cyclops', name: 'Cyclops', body: 0x4b, soundFamily: 'cyclops', slayerGroup: 'repond' }, // 75. VERIFY
  // The dark casters.
  { id: 'lich', name: 'Lich', body: 0x18, soundFamily: 'liche', slayerGroup: 'undead', talismanSlayers: ['mage'] }, // 24. VERIFY (reuses lich-lord family)
  { id: 'lich-lord', name: 'Lich Lord', body: 0x4f, soundFamily: 'liche', slayerGroup: 'undead', talismanSlayers: ['mage'] }, // 79. VERIFY
  { id: 'ophidian-mage', name: 'Ophidian Mage', body: 0x55, soundFamily: 'snake-mage', slayerGroup: 'reptile', lesserSlayers: ['ophidian'] }, // 85. VERIFY
  { id: 'ophidian-warrior', name: 'Ophidian Warrior', body: 0x56, soundFamily: 'snake-mage', slayerGroup: 'reptile', lesserSlayers: ['ophidian'] }, // 86. VERIFY (ophidian reuse)
  { id: 'blood-elemental', name: 'Blood Elemental', body: 0x9f, hue: 37, soundFamily: 'watrel', slayerGroup: 'elemental', lesserSlayers: ['blood-elemental'] }, // Body.def 159 -> 16, hue 37.
  // The elementals.
  { id: 'air-elemental', name: 'Air Elemental', body: 0xd, hue: 0x4001, soundFamily: 'air-elemental', slayerGroup: 'elemental', lesserSlayers: ['air-elemental'] }, // ServUO spectral hue.
  { id: 'earth-elemental', name: 'Earth Elemental', body: 0xe, soundFamily: 'earth-elemental', slayerGroup: 'elemental', lesserSlayers: ['earth-elemental'] }, // 14. VERIFY
  { id: 'fire-elemental', name: 'Fire Elemental', body: 0xf, soundFamily: 'fire-elemental', slayerGroup: 'elemental', lesserSlayers: ['fire-elemental'], talismanSlayers: ['flame'] }, // 15. VERIFY
  { id: 'water-elemental', name: 'Water Elemental', body: 0x10, soundFamily: 'watrel', slayerGroup: 'elemental', lesserSlayers: ['water-elemental'] }, // 16. VERIFY (278 = watrel)
  // The terathans.
  { id: 'terathan-warrior', name: 'Terathan Warrior', body: 0x46, soundFamily: 'terathan', slayerGroup: 'arachnid', lesserSlayers: ['terathan'] }, // 70. VERIFY
  { id: 'terathan-drone', name: 'Terathan Drone', body: 0x47, soundFamily: 'terathan', slayerGroup: 'arachnid', lesserSlayers: ['terathan'] }, // 71. VERIFY
  { id: 'terathan-avenger', name: 'Terathan Avenger', body: 0x98, hue: 1653, soundFamily: 'terathan', slayerGroup: 'arachnid', lesserSlayers: ['terathan'] }, // Body.def 152 -> 70, hue 1653.
  // The giants.
  { id: 'efreet', name: 'Efreet', body: 0x83, hue: 1654, soundFamily: 'exodus', slayerGroup: 'elemental' }, // Body.def 131 -> 13, hue 1654.
  { id: 'arctic-ogre-lord', name: 'Arctic Ogre Lord', body: 0x87, hue: 1154, soundFamily: 'ogre', slayerGroup: 'repond', lesserSlayers: ['ogre'] }, // Body.def 135 -> 1, hue 1154.
  { id: 'titan', name: 'Titan', body: 0x4c, soundFamily: 'cyclops-boss', slayerGroup: 'repond' }, // 76. VERIFY
  // The endgame - daemons & dragonkin.
  { id: 'daemon', name: 'Daemon', body: 0x9, soundFamily: 'daemon', slayerGroup: 'demon' }, // 9. VERIFY
  { id: 'gate-daemon', name: 'Gate Daemon', body: 0x26, soundFamily: 'daemon', slayerGroup: 'demon' }, // 38. VERIFY
  { id: 'ice-fiend', name: 'Ice Fiend', body: 0x2b, hue: 1152, soundFamily: 'daemon', slayerGroup: 'demon', talismanSlayers: ['ice'] }, // Body.def 43 -> 9, hue 1152.
  { id: 'wyvern', name: 'Wyvern', body: 0x3e, hue: 1810, soundFamily: 'dragon', slayerGroup: 'reptile', lesserSlayers: ['dragon'] }, // Body.def 62 -> 60, hue 1810.
  { id: 'drake', name: 'Drake', body: 0x3c, soundFamily: 'dragon', slayerGroup: 'reptile', lesserSlayers: ['dragon'] }, // 60. VERIFY (ServUO randomises 60/61; we pin 60)
  { id: 'dragon', name: 'Dragon', body: 0xc, soundFamily: 'dragon', slayerGroup: 'reptile', lesserSlayers: ['dragon'] }, // 12. VERIFY
  { id: 'white-wyrm', name: 'White Wyrm', body: 0xb4, hue: 1153, soundFamily: 'wyrm', slayerGroup: 'reptile', lesserSlayers: ['dragon'] }, // Body.def 180 -> 59, hue 1153.
  { id: 'shadow-wyrm', name: 'Shadow Wyrm', body: 0x6a, hue: 0x5275, soundFamily: 'wyrm', slayerGroup: 'reptile', lesserSlayers: ['dragon'] }, // Body.def spectral hue.
  { id: 'ancient-wyrm', name: 'Ancient Wyrm', body: 0x69, hue: 1402, soundFamily: 'wyrm', slayerGroup: 'reptile', lesserSlayers: ['dragon'] }, // Body.def 105 -> 12, hue 1402.
  // Classic Britannia/T2A card expansion. These identities intentionally do not
  // enter Arena yet. Their stand-only bake records the resolved anim graphic and
  // storage layout so card art does not accidentally crop a run or attack frame.
  { id: 'alligator', name: 'Alligator', body: 0xca, cardOnly: true, animationSet: 'animal', slayerGroup: 'reptile' },
  { id: 'black-bear', name: 'Black Bear', body: 211, cardOnly: true, animationSet: 'animal', slayerGroup: null, talismanSlayers: ['bear'] },
  { id: 'brown-bear', name: 'Brown Bear', body: 167, hue: 443, cardOnly: true, baseGraphic: 211, animationSet: 'animal', slayerGroup: null, talismanSlayers: ['bear'] },
  { id: 'grizzly-bear', name: 'Grizzly Bear', body: 212, cardOnly: true, animationSet: 'animal', slayerGroup: null, talismanSlayers: ['bear'] },
  { id: 'polar-bear', name: 'Polar Bear', body: 213, cardOnly: true, animationSet: 'animal', slayerGroup: null, talismanSlayers: ['bear'] },
  { id: 'cougar', name: 'Cougar', body: 63, cardOnly: true, baseGraphic: 214, animationSet: 'animal', slayerGroup: null },
  { id: 'panther', name: 'Panther', body: 214, hue: 2305, cardOnly: true, animationSet: 'animal', slayerGroup: null },
  { id: 'giant-rat', name: 'Giant Rat', body: GIANT_RAT_BODY, cardOnly: true, animationSet: 'animal', slayerGroup: null },
  { id: 'giant-toad', name: 'Giant Toad', body: 80, cardOnly: true, animationSet: 'monster', slayerGroup: null },
  { id: 'giant-serpent', name: 'Giant Serpent', body: 21, hue: 2009, cardOnly: true, animationSet: 'monster', slayerGroup: 'reptile', lesserSlayers: ['snake'] },
  { id: 'frost-spider', name: 'Frost Spider', body: 20, hue: 1154, cardOnly: true, baseGraphic: 28, animationSet: 'monster', slayerGroup: 'arachnid', lesserSlayers: ['spider'], talismanSlayers: ['ice'] },
  { id: 'frost-troll', name: 'Frost Troll', body: 55, hue: 1154, cardOnly: true, baseGraphic: 53, animationSet: 'monster', slayerGroup: 'repond', lesserSlayers: ['troll'], talismanSlayers: ['ice'] },
  { id: 'lava-lizard', name: 'Lava Lizard', body: 206, hue: 1643, cardOnly: true, animationSet: 'animal', slayerGroup: 'reptile', talismanSlayers: ['flame'] },
  { id: 'nightmare', name: 'Nightmare', body: 116, hue: 1109, cardOnly: true, baseGraphic: 200, animationSet: 'animal', slayerGroup: null },
  { id: 'forest-ostard', name: 'Forest Ostard', body: 219, hue: 0x88a4, cardOnly: true, animationSet: 'animal', slayerGroup: null, talismanSlayers: ['bird'] },
  { id: 'desert-ostard', name: 'Desert Ostard', body: 210, cardOnly: true, animationSet: 'animal', slayerGroup: null, talismanSlayers: ['bird'] },
  { id: 'frenzied-ostard', name: 'Frenzied Ostard', body: 218, hue: 0x8465, cardOnly: true, animationSet: 'animal', slayerGroup: null, talismanSlayers: ['bird'] },
  { id: 'elder-gazer', name: 'Elder Gazer', body: 22, cardOnly: true, animationSet: 'monster', slayerGroup: null },
  { id: 'wisp', name: 'Wisp', body: 58, cardOnly: true, animationSet: 'monster', slayerGroup: 'fey' },
  { id: 'swamp-tentacle', name: 'Swamp Tentacle', body: 66, hue: 61, cardOnly: true, baseGraphic: 8, animationSet: 'monster', slayerGroup: null },
  { id: 'orcish-lord', name: 'Orcish Lord', body: 138, cardOnly: true, baseGraphic: 7, animationSet: 'monster', slayerGroup: 'repond', lesserSlayers: ['orc'] },
  { id: 'orcish-mage', name: 'Orcish Mage', body: 140, hue: 0x8579, cardOnly: true, baseGraphic: 17, animationSet: 'monster', slayerGroup: 'repond', lesserSlayers: ['orc'], talismanSlayers: ['mage'] },
  { id: 'terathan-matriarch', name: 'Terathan Matriarch', body: 72, cardOnly: true, animationSet: 'monster', mountable: true, slayerGroup: 'arachnid', lesserSlayers: ['terathan'] },
  { id: 'ophidian-knight-errant', name: 'Ophidian Knight-Errant', body: 86, cardOnly: true, animationSet: 'monster', slayerGroup: 'reptile', lesserSlayers: ['ophidian'] },
  { id: 'ophidian-matriarch', name: 'Ophidian Matriarch', body: 87, cardOnly: true, animationSet: 'monster', slayerGroup: 'reptile', lesserSlayers: ['ophidian'] },
  { id: 'ice-elemental', name: 'Ice Elemental', body: 161, hue: 1152, cardOnly: true, baseGraphic: 14, animationSet: 'monster', slayerGroup: 'elemental', lesserSlayers: ['snow-elemental'], talismanSlayers: ['ice'] },
  { id: 'poison-elemental', name: 'Poison Elemental', body: 162, hue: 67, cardOnly: true, baseGraphic: 13, animationSet: 'monster', slayerGroup: 'elemental', lesserSlayers: ['poison-elemental'] },
  { id: 'snow-elemental', name: 'Snow Elemental', body: 163, hue: 1153, cardOnly: true, baseGraphic: 13, animationSet: 'monster', slayerGroup: 'elemental', lesserSlayers: ['snow-elemental'], talismanSlayers: ['ice'] },
  { id: 'sea-serpent', name: 'Sea Serpent', body: 150, hue: 1332, cardOnly: true, animationSet: 'monster', slayerGroup: 'reptile', lesserSlayers: ['snake'] },
  { id: 'deep-sea-serpent', name: 'Deep Sea Serpent', body: 150, hue: 2210, cardOnly: true, animationSet: 'monster', slayerGroup: 'reptile', lesserSlayers: ['snake'] },
  { id: 'kraken', name: 'Kraken', body: 77, hue: 91, cardOnly: true, baseGraphic: 8, animationSet: 'monster', slayerGroup: null },
  { id: 'blade-spirit', name: 'Blade Spirit', body: 574, cardOnly: true, animationSet: 'monster', slayerGroup: null },
  { id: 'energy-vortex', name: 'Energy Vortex', body: ENERGY_VORTEX_BODY, hue: ENERGY_VORTEX_HUE, cardOnly: true, baseGraphic: ENERGY_VORTEX_BASE_GRAPHIC, animationSet: 'monster', slayerGroup: null },
  // Core Set v3 card-only additions. These four identities are deliberately
  // registry-owned even though they do not enter Arena: the converter/stubber,
  // card art, Slayer classification, and live set all derive from this row.
  { id: 'pixie', name: 'Pixie', body: 128, cardOnly: true, animationSet: 'monster', slayerGroup: 'fey' },
  { id: 'treefellow-guardian', name: 'Treefellow Guardian', body: 301, cardOnly: true, animationSet: 'monster', slayerGroup: 'fey' },
  { id: 'copper-elemental', name: 'Copper Elemental', body: 109, cardOnly: true, animationSet: 'monster', slayerGroup: 'elemental' },
  { id: 'phoenix', name: 'Phoenix', body: 0x340, cardOnly: true, animationSet: 'monster', slayerGroup: 'fey', talismanSlayers: ['bird', 'flame'] },
  // The arena's wandering elite (the Abysmal Horror, ADR-0217 - swapped off the
  // Darknight Creeper) and its rotating bosses (ADR-0211). Boss bodies are absent
  // from every wave tier (isArenaBossBody must not misfire). The Abysmal Horror
  // carries its own ServUO sound set (BaseSoundID 0x451 = the `mushroom_men`
  // recording); the Darknight Creeper stays registered but is no longer spawned.
  { id: 'abysmal-horror', name: 'Abysmal Horror', body: ABYSMAL_HORROR_BODY, soundFamily: 'abysmal-horror', slayerGroup: 'demon' }, // 312 = 0x138
  { id: 'darknight-creeper', name: 'Darknight Creeper', body: DARKNIGHT_CREEPER_BODY, soundFamily: 'walrus', slayerGroup: 'undead' },
  // The progressive-elites mini-bosses (0x12F-0x13F band). Like the elite above they
  // are absent from every wave tier so isArenaBossBody can't misfire on a wave spawn.
  { id: 'gibberling', name: 'Gibberling', body: GIBBERLING_BODY, soundFamily: 'mongbat', slayerGroup: 'demon' }, // 307 = 0x133
  { id: 'mound-of-maggots', name: 'Mound of Maggots', body: MOUND_OF_MAGGOTS_BODY, soundFamily: 'slime', slayerGroup: null }, // 319 = 0x13f
  { id: 'patchwork-skeleton', name: 'Patchwork Skeleton', body: PATCHWORK_SKELETON_BODY, soundFamily: 'skeleton', slayerGroup: 'undead' }, // 309 = 0x135
  { id: 'bone-demon', name: 'Bone Demon', body: BONE_DEMON_BODY, soundFamily: 'daemon', slayerGroup: 'demon' }, // 308 = 0x134
  { id: 'gore-fiend', name: 'Gore Fiend', body: GORE_FIEND_BODY, soundFamily: 'daemon', slayerGroup: 'undead' }, // 305 = 0x131
  { id: 'devourer', name: 'Devourer of Souls', body: DEVOURER_BODY, soundFamily: 'daemon', slayerGroup: 'demon' }, // 303 = 0x12f
  { id: 'flesh-golem', name: 'Flesh Golem', body: FLESH_GOLEM_BODY, soundFamily: 'zombie', slayerGroup: 'undead' }, // 304 = 0x130
  { id: 'impaler', name: 'Impaler', body: IMPALER_BODY, soundFamily: 'reaper', slayerGroup: 'demon' }, // 306 = 0x132
  { id: 'wanderer', name: 'Wanderer of the Void', body: WANDERER_BODY, soundFamily: 'shadows', slayerGroup: null }, // 316 = 0x13c
  { id: 'demon-knight', name: 'Demon Knight', body: DEMON_KNIGHT_BODY, soundFamily: 'daemon', slayerGroup: 'demon' }, // elite #12
  // The opening-climax boss sprite (ADR-0211): Lord Blackthorn wears a demon body
  // (0x66 = 102), the always-present first boss. Dedicated - absent from every wave
  // tier so isArenaBossBody can't misfire. Full bestiary attack set. VERIFY at bake.
  { id: 'demon-lord', name: 'Demon Lord', body: 0x66, soundFamily: 'daemon', slayerGroup: 'demon' }, // 102 = 0x66. VERIFY (357 = daemon)
  // The former opening-boss sprite: a dedicated Demon Knight body (0x2E5), distinct
  // from the elite Demon Knight (0x13E). Kept registered (bestiary creatures may sit
  // unused, like slime/snake) though no roster entry wears it now that Blackthorn
  // moved to the demon body above.
  { id: 'demon-knight-boss', name: 'The Demon Knight', body: DEMON_KNIGHT_BOSS_BODY, soundFamily: 'daemon', slayerGroup: 'demon' }, // 741 = 0x2e5
  { id: 'balron', name: 'Balron', body: 0x28, hue: 1110, soundFamily: 'daemon', slayerGroup: 'demon' }, // Body.def 40 -> 10, hue 1110.
  { id: 'rikktor', name: 'Rikktor', body: 0xac, soundFamily: 'dragon', slayerGroup: 'reptile', lesserSlayers: ['dragon'] }, // 172. VERIFY (no BaseSoundID in ServUO; dragonkin reuse)
  { id: 'mephitis', name: 'Mephitis', body: 0xad, hue: 2415, soundFamily: 'spider', slayerGroup: 'arachnid', lesserSlayers: ['spider'] }, // Body.def 173 -> 28, hue 2415.
  { id: 'semidar', name: 'Semidar', body: 0xae, hue: 2418, soundFamily: 'semidar', slayerGroup: 'demon' }, // Body.def 174 -> 30, hue 2418.
]);

/** Look a bestiary monster up by id (`undefined` for an unknown id). */
export function monsterById(id: string): MonsterDefinition | undefined {
  return monsters.has(id) ? monsters.get(id) : undefined;
}

/** Every world/Arena bestiary body id, for the O(1) body predicate below. */
const MONSTER_BODIES: ReadonlySet<number> = new Set(
  monsters.all().filter((m) => !m.cardOnly).map((m) => m.body),
);

/**
 * Whether a UO body id belongs to the {@link monsters bestiary} (ADR-0184).
 * Bestiary creatures read as **scenery** to the renderer exactly like
 * {@link isStormBody storm bodies} - no duel-target hover/click, no war-target
 * wiring, no context-menu rows, no corpse-look capture - because they only
 * ever appear as minigame enemies (killed by the mode's own mechanics, never
 * duelled). The client identifies them by the replicated body id; no new
 * snapshot field (the isStormBody precedent).
 */
export function isMonsterBody(body: number | undefined): boolean {
  return body !== undefined && MONSTER_BODIES.has(body);
}

// ---------------------------------------------------------------------------
// Monster sounds (ADR-0187). Every bestiary creature carries four action sounds
// - move (its ambient idle voice, sounded as it roams), attack, hurt (damaged),
// die - sourced from the classic UO per-creature recording sets. The mapping is
// ServUO's: each creature has a BaseSoundID and the five sounds sit at fixed
// offsets (anger +0 / idle +1 / attack +2 / hurt +3 / death +4); the `file`
// basenames below are the exact strings embedded in `sound.mul` at those
// indices (parsed from a real client's mul, calibrated against the captain's
// extracted pack - e.g. slime 456 -> `slime2`..`slime5`).
//
// Creatures are grouped into **sound families** because the sharing is
// canonical, not an economy: all three orcs use one ServUO BaseSoundID (1114),
// the Mummy literally uses the Zombie set (both 471), the Demon Knight and Gate
// Daemon use the plain Daemon set (357), and the mul's two skeleton recording
// sets carry identical embedded names (452-455 vs 1166-1169) so an extracted
// pack holds one. Quirky-but-authentic reuses: Blood Elemental = water
// elemental (`watrel`), Efreet = Exodus, Titan = cyclops boss, Darknight
// Creeper = walrus, Ancient Wyrm = Dragon plus ServUO's red-dragon idle/hurt
// overrides (723/721). Each family registers four `mon-<family>-<role>` rows in
// the {@link sounds} registry, so the converter/stub bake them like every other
// sound; a family's `anger` (+0) sound is deliberately unregistered - nothing
// plays it.
// ---------------------------------------------------------------------------

/** The bestiary sound families (ADR-0187): one per classic UO recording set. */
export const MONSTER_SOUND_FAMILIES = [
  'slime',
  'snake',
  'mongbat',
  'shadows',
  'zombie',
  'skeleton',
  'orc',
  'lizardman',
  'liche',
  'snake-mage',
  'watrel',
  'exodus',
  'ogre',
  'cyclops',
  'cyclops-boss',
  'daemon',
  'dragon',
  'wyrm',
  'walrus',
  // Added with the ADR-0211 bestiary expansion (BaseSoundID in the files table).
  'spider',
  'scorpion',
  'wolf',
  'harpy',
  'corpser',
  'reaper',
  'ratman',
  'ettin',
  'troll',
  'gazer',
  'headless',
  'gargoyle',
  'air-elemental',
  'earth-elemental',
  'fire-elemental',
  'terathan',
  'semidar',
  // The arena elite's own set (ADR-0217): the Abysmal Horror's ServUO BaseSoundID
  // 0x451 (1105), which is the `mushroom_men` recording in the client's sound.mul.
  'abysmal-horror',
] as const;
export type MonsterSoundFamily = (typeof MONSTER_SOUND_FAMILIES)[number];

/** The four per-creature action sounds (ADR-0187). `move` is UO's idle voice (+1). */
export const MONSTER_SOUND_ROLES = ['move', 'attack', 'hurt', 'die'] as const;
export type MonsterSoundRole = (typeof MONSTER_SOUND_ROLES)[number];

/** A monster sound's registry/baked-asset id: `mon-<family>-<role>`. */
export type MonsterSoundId = `mon-${MonsterSoundFamily}-${MonsterSoundRole}`;

/**
 * Source `.wav` basenames per family x role - the exact embedded `sound.mul` names at
 * BaseSoundID +1/+2/+3/+4 (see the block comment above for provenance). VERIFY at
 * bake: `npm run convert` warns per missing basename and the stub covers dev/CI.
 */
const MONSTER_SOUND_FILES: Record<MonsterSoundFamily, Record<MonsterSoundRole, string>> = {
  slime: { move: 'slime2', attack: 'slime3', hurt: 'slime4', die: 'slime5' }, // 456
  snake: { move: 'snake2', attack: 'snake3', hurt: 'snake4', die: 'snake5' }, // 219
  mongbat: { move: 'mngbat2', attack: 'mngbat3', hurt: 'mngbat4', die: 'mngbat5' }, // 422
  shadows: { move: 'shadows2', attack: 'shadows3', hurt: 'shadows4', die: 'shadows5' }, // 1154
  zombie: { move: 'zombie2', attack: 'zombie3', hurt: 'zombie4', die: 'zombie5' }, // 471
  skeleton: { move: 'skletn2', attack: 'skletn3', hurt: 'skletn4', die: 'skletn5' }, // 451/1165
  orc: { move: 'orc2_2', attack: 'orc2_3', hurt: 'orc2_4', die: 'orc2_5' }, // 1114
  lizardman: { move: 'lzrdmn2', attack: 'lzrdmn3', hurt: 'lzrdmn4', die: 'lzrdmn5' }, // 417
  liche: { move: 'liche2', attack: 'liche3', hurt: 'liche4', die: 'liche5' }, // 412
  'snake-mage': {
    move: 'snake_mage_2',
    attack: 'snake_mage_3',
    hurt: 'snake_mage_4',
    die: 'snake_mage_5',
  }, // 639
  watrel: { move: 'watrel2', attack: 'watrel3', hurt: 'watrel4', die: 'watrel5' }, // 278
  exodus: { move: 'exodus2', attack: 'exodus3', hurt: 'exodus4', die: 'exodus5' }, // 768
  ogre: { move: 'ogre2', attack: 'ogre3', hurt: 'ogre4', die: 'ogre5' }, // 427
  cyclops: { move: 'cyclops_2', attack: 'cyclops_3', hurt: 'cyclops_4', die: 'cyclops_5' }, // 604
  'cyclops-boss': {
    move: 'cyclops_boss_2',
    attack: 'cyclops_boss_3',
    hurt: 'cyclops_boss_4',
    die: 'cyclops_boss_5',
  }, // 609
  daemon: { move: 'daemon2', attack: 'daemon3', hurt: 'daemon4', die: 'daemon5' }, // 357
  dragon: { move: 'dragon2', attack: 'dragon3', hurt: 'dragon4', die: 'dragon5' }, // 362
  // Dragon's set with ServUO AncientWyrm's explicit idle (723) / hurt (721) overrides.
  wyrm: { move: 'dragon_red_run', attack: 'dragon3', hurt: 'dragon_red_gethit', die: 'dragon5' },
  walrus: { move: 'walrus2', attack: 'walrus3', hurt: 'walrus4', die: 'walrus5' }, // 224
  // ADR-0211 additions. Basenames are the embedded sound.mul names at BaseSoundID
  // +1/+2/+3/+4; all VERIFY at the captain's bake (a missing basename warns and the
  // silent stub covers dev/CI, exactly like every family above).
  spider: { move: 'gispidr2', attack: 'gispidr3', hurt: 'gispidr4', die: 'gispidr5' }, // 904
  scorpion: { move: 'gscorp2', attack: 'gscorp3', hurt: 'gscorp4', die: 'gscorp5' }, // 397
  wolf: { move: 'wolf2', attack: 'wolf3', hurt: 'wolf4', die: 'wolf5' }, // 229
  harpy: { move: 'harpy2', attack: 'harpy3', hurt: 'harpy4', die: 'harpy5' }, // 402
  corpser: { move: 'corpsr2', attack: 'corpsr3', hurt: 'corpsr4', die: 'corpsr5' }, // 684
  reaper: { move: 'reaper2', attack: 'reaper3', hurt: 'reaper4', die: 'reaper5' }, // 442
  ratman: { move: 'ratman2', attack: 'ratman3', hurt: 'ratman4', die: 'ratman5' }, // 437
  ettin: { move: 'ettin2', attack: 'ettin3', hurt: 'ettin4', die: 'ettin5' }, // 367
  troll: { move: 'troll2', attack: 'troll3', hurt: 'troll4', die: 'troll5' }, // 461
  gazer: { move: 'gazer2', attack: 'gazer3', hurt: 'gazer4', die: 'gazer5' }, // 377
  headless: { move: 'headls2', attack: 'headls3', hurt: 'headls4', die: 'headls5' }, // 925
  gargoyle: { move: 'gargyl2', attack: 'gargyl3', hurt: 'gargyl4', die: 'gargyl5' }, // 372
  'air-elemental': { move: 'air02', attack: 'air03', hurt: 'air04', die: 'air05' }, // 655 (zero-padded in the mul)
  'earth-elemental': { move: 'erthel2', attack: 'erthel3', hurt: 'erthel4', die: 'erthel5' }, // 268
  'fire-elemental': { move: 'fire02', attack: 'fire03', hurt: 'fire04', die: 'fire05' }, // 838 (zero-padded in the mul)
  // Terathan's mul set uses the literal "spider_warrior" recording (underscored, not abbreviated).
  terathan: { move: 'spider_warrior_2', attack: 'spider_warrior_3', hurt: 'spider_warrior_4', die: 'spider_warrior_5' }, // 589
  // Semidar's mul set is the succubus recording.
  semidar: { move: 'succubus2', attack: 'succubus3', hurt: 'succubus4', die: 'succubus5' }, // 1200
  // The Abysmal Horror (arena elite, ADR-0217): ServUO BaseSoundID 0x451 (1105) is the
  // `mushroom_men` recording (move/attack/hurt/die at +1/+2/+3/+4 = 1106-1109). CONFIRMED
  // against the client's soundidx.mul/sound.mul (embedded names), not merely VERIFY.
  'abysmal-horror': {
    move: 'mushroom_men2',
    attack: 'mushroom_men3',
    hurt: 'mushroom_men4',
    die: 'mushroom_men5',
  }, // 1105
};

/** The `mon-<family>-<role>` sound id a monster sound registers and bakes under. */
export function monsterSoundId(family: MonsterSoundFamily, role: MonsterSoundRole): MonsterSoundId {
  return `mon-${family}-${role}`;
}

/** The generated {@link sounds} rows: four per family, in registry declaration order. */
function monsterSoundEntries(): { id: MonsterSoundId; file: string }[] {
  return MONSTER_SOUND_FAMILIES.flatMap((family) =>
    MONSTER_SOUND_ROLES.map((role) => ({
      id: monsterSoundId(family, role),
      file: MONSTER_SOUND_FILES[family][role],
    })),
  );
}

/** Body id -> sound family, for the client's replicated-body cue lookup (ADR-0187). */
const MONSTER_SOUND_FAMILY_BY_BODY: ReadonlyMap<number, MonsterSoundFamily> = new Map(
  monsters.all().flatMap((m) => m.soundFamily ? [[m.body, m.soundFamily]] : []),
);

/**
 * The sound family of a bestiary body id, or `undefined` for anything else. The
 * client keys every monster cue (move/attack/hurt/die) off the replicated body id
 * through this - the {@link isMonsterBody} discriminator's audio twin (ADR-0187).
 */
export function monsterSoundFamilyForBody(body: number | undefined): MonsterSoundFamily | undefined {
  return body === undefined ? undefined : MONSTER_SOUND_FAMILY_BY_BODY.get(body);
}

/** Body id -> display name, for herald/warning text that names the creature by its replicated body. */
const MONSTER_NAME_BY_BODY: ReadonlyMap<number, string> = new Map(
  monsters.all().filter((m) => !m.cardOnly).map((m) => [m.body, m.name]),
);

/**
 * The display name of a bestiary body id (e.g. the elite-entrance warning naming
 * the exact creature), or `undefined` for anything else. Body -> name is unique.
 */
export function monsterNameForBody(body: number | undefined): string | undefined {
  return body === undefined ? undefined : MONSTER_NAME_BY_BODY.get(body);
}

/**
 * The render-stack slot an equippable cosmetic occupies. UO draws a clothed
 * character as the base body plus a stack of equipment layers, each its own
 * animation keyed by the same `(action, facing, frame)` as the body and drawn in
 * a facing-dependent order (see ADR-0030). `hair`/`beard` (Slices 1-2), the
 * curated garment slots `pants`/`skirt`/`shirt`/`doublet` (Slice 3), and `robe`/
 * `cloak` (Slice 4, #57) are all populated. Activating robe/cloak was pure data:
 * the layer-order table already covered both, so they added registry entries and a
 * splash picker, not new render plumbing (the forward-looking slots paid off).
 */
export type EquipLayer =
  | 'hair'
  | 'beard'
  | 'shirt'
  | 'pants'
  | 'skirt'
  | 'feet'
  | 'doublet'
  | 'sash'
  | 'dress'
  | 'apron'
  | 'robe'
  | 'cloak'
  | 'accessory'
  | 'headgear'
  // The first armor/weapon slots (The King's Justice guard kit, PRD #361, ADR-0194):
  // distinct from the cloth slots so plate never collides with a rolled cosmetic on
  // the same layer, and `weapon` is the in-the-hand slot drawn over everything.
  | 'armor-chest'
  | 'armor-arms'
  | 'armor-legs'
  | 'weapon';

/**
 * An equippable cosmetic: a UO anim graphic drawn over the body as an extra
 * render layer. `id` is the friendly content id (e.g. `hair.short`, used in baked
 * asset filenames and on the entity's `equipment`); `layer` is the render slot it
 * occupies; `anim` is the UO anim graphic id the converter decodes from the same
 * `anim.mul` path as a body (resolved from a wearable's `tiledata.mul` Animation
 * field, not its item id - see the {@link equipment} registry). `bodies` restricts which bodies the layer is valid for
 * and assembled on at render time - createSprite skips a layer whose `bodies`
 * excludes the character's body (default both); it is not a bake-time restriction,
 * since the equip atlas is keyed by content id and the converter/stub bake one atlas
 * per id regardless. `partialHue` marks cloth dyes that only recolour near-gray pixels (false
 * for hair, which - like the body - is fully hued and needs no extra shader).
 *
 * Equipment is **purely cosmetic identity**, parallel to `bodies`/`hues`: no
 * inventory, no equip command, no sim state. It rides the entity's renderable
 * exactly as body/hue/name do (see ADR-0030).
 */
export interface EquipDefinition extends Definition {
  layer: EquipLayer;
  /**
   * The equipment anim graphic the bake decodes this layer from. Either this **or**
   * {@link itemGraphic} must be set: the hair/beard/garment layers pin a
   * captain-confirmed anim number directly, while a weapon resolves its anim from the
   * item's tiledata `Animation` field via {@link itemGraphic} (so a wrong guess can
   * never again decode the wrong held item - ADR-0237 follow-up).
   */
  anim?: number;
  /**
   * The UO static art id of the underlying item (e.g. the bow `0x13B2`). When set, the
   * equip bake resolves {@link anim} from this item's tiledata `Animation` field
   * instead of a hardcoded number - the self-correcting path for held weapons. Only
   * one of `anim`/`itemGraphic` is set per def.
   */
  itemGraphic?: number;
  bodies?: number[];
  partialHue?: boolean;
  /**
   * Optional world-sprite multiplier for this one equipment layer (default 1).
   * Unlike `RenderableComponent.scale`, this grows only the wearable while the
   * body and every sibling layer stay at their normal size.
   */
  visualScale?: number;
  /**
   * Whether this is a player-earnable cosmetic offered in the unlock roll and the
   * wardrobe (default `true`). A **system** layer the game assembles itself - the
   * death shroud ({@link SHROUD_ID}) a downed duelist wears - sets `false` so it is
   * baked/loaded like any other anim graphic but never rolled, unlocked, or shown as
   * a pickable style. See {@link ../cosmeticRoll.itemsForBody}.
   */
  rollable?: boolean;
}

/**
 * Named hues. `player` (1009) is a warm human skin tone; `npc` (2) is a distinct
 * deep colour so two characters read as separate people. Players are assigned a
 * hue at connect; these are the defaults the bootstrap seeds.
 */
export const hues = new Registry<HueDefinition>('hue', [
  { id: 'player', hue: 1009 },
  { id: 'npc', hue: 2 },
]);

/**
 * The valid hue ids a cosmetic unlock can roll in (Cosmetic Progression Slice B,
 * PRD #101 / ADR-0044). An unlock's hue is drawn **uniformly** from this list, so
 * each entry is an equally-likely, distinct collectible (owning `hair.long` in one
 * hue does not imply owning it in another). It enumerates the **populated**
 * cosmetic-dye bands - the olive-green ramp, the named dye strip, and the
 * pink/blue/green/orange ramps - every id of which resolves to a real `hues.mul`
 * ramp, rather than the full 0..~3000 range (most of which is empty and would
 * recolour to nothing). Plain `sim/` data with no asset read, so the roll engine stays inside
 * the `architecture.test.ts` boundary; the server hands it to the picker as
 * `validHues`. Skin tones are deliberately excluded - skin is body identity, not a
 * cosmetic drop. Widen this list as more populated hue ids are confirmed against a
 * converted `hues.mul`.
 *
 * The 2026-07-03 widening: the captain walked the client's hue table in UOFiddler
 * and attached the populated bands to add - the classic dye pages (1-63, 69-131,
 * 149-211), the muted/dusk page (593-655), the hair-dye band (1102-1149, the rows
 * hues.mul names `HairHue #1101`-`#1148`), and the shard's custom named strip
 * (2761-2785, "Ash Brown" through "Umbrascale Red"). Rows 1097-1101 from that walk
 * are deliberately EXCLUDED: their hues.mul names are placeholder junk (`?-`, `-`,
 * blank, `me`) and 1097-1099 decode to uninitialised checkerboard noise.
 */

/** Every id from `first` to `last` inclusive - one populated hues.mul band. */
function hueRange(first: number, last: number): number[] {
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}

export const COSMETIC_HUE_IDS: readonly number[] = [
  // The classic dye pages (captain-attached, 2026-07-03): blues/purples/pinks/
  // reds/golds (1-63), greens/teals/blues (69-131), yellows/greens/blues (149-211).
  // Hue 1 is DELIBERATELY RESERVED for rare/sellable cosmetics (ADR-0186) and is
  // intentionally excluded from this auto-drop pool - the band starts at 2, not 1.
  // Do not "helpfully" widen it back to 1: hue 1 stays a valid hue the game can
  // still assign deliberately elsewhere, it just can never be an automatic drop.
  ...hueRange(2, 63),
  ...hueRange(69, 131),
  ...hueRange(149, 211),
  // The muted dusk/berry page (captain-attached, 2026-07-03).
  ...hueRange(593, 655),
  // Olive-green ramp.
  ...hueRange(757, 766),
  // Blue.
  897,
  // The hair-dye band (`HairHue #1101`-`#1148`; captain-attached, 2026-07-03).
  // Rolls like any cosmetic hue - hair is fully hued, cloth partial-hues it.
  ...hueRange(1102, 1149),
  // The named dye strip (ice / darks / brights / gold).
  ...hueRange(1150, 1177),
  // Pinks ramp (1201-1254) and its fire-weapon tail (1255-1258).
  ...hueRange(1201, 1258),
  // Blues ramp.
  ...hueRange(1301, 1330),
  // Greens ramp.
  ...hueRange(1409, 1438),
  // Oranges ramp.
  ...hueRange(1509, 1538),
  // The shard's custom named strip ("Ash Brown" .. "Umbrascale Red";
  // captain-attached, 2026-07-03).
  ...hueRange(2761, 2785),
];

/**
 * The land tiles a map is built from. The friendly ids are what a map's palette
 * references; the `artId`s are the classic grass/dirt/water diamonds the
 * converter bakes.
 */
export const tiles = new Registry<TileDefinition>('tile', [
  { id: 'grass', artId: 0x0003 },
  { id: 'dirt', artId: 0x0009 },
  { id: 'water', artId: 0x00a8 },
]);

/**
 * The named statics the authored/test maps place. The friendly ids are what a
 * map's static placements reference; the `artId`s are the classic tree and wall
 * segments. The extracted WBB map references its statics by raw `static-0x…`
 * id instead (see `tools/anim-converter/mapRefs.ts`); the converter and stub bake
 * the union of these and those. `0x0CE3` is a classic tree; `0x0006` is the
 * authored-map wall and `0x0057` is the Sphere Duel wall.
 */
export const statics = new Registry<StaticDefinition>('static', [
  { id: 'tree', artId: 0x0ce3 },
  { id: 'wall', artId: 0x0006 },
  { id: 'sphere-wall', artId: 0x0057 },
  { id: 'static-0xf5e', artId: 0x0f5e },
]);

/**
 * Trading-card equipment illustrations. These are ordinary UO item statics, but
 * live outside the map-placement registry so card-only art cannot become world
 * content accidentally. The converter appends them to the shared static atlas.
 */
export const cardArtStatics = new Registry<StaticDefinition>('card art static', [
  { id: 'card-yellow-potion', artId: 0x0f0c },
  { id: 'card-purple-potion', artId: 0x0f0d },
  { id: 'card-pouch', artId: 0x0e79 },
  { id: 'card-scroll', artId: 0x1f49 },
]);

/**
 * The mouse cursors the converter bakes from the UO client's static art: the
 * classic **gauntlet** viewport cursor (issue #51, #54) - an armoured glove
 * pointing a finger - which `gameCursor.ts` paints over the game viewport, plus
 * the eight **directional** gauntlets that preview the direction a viewport
 * right-click would move (#66, ADR-0408).
 * Both the real converter and the placeholder stub walk this registry, so the
 * baked cursor set can never drift from what the runtime imports.
 *
 * `artId` is the raw UO **static** art id (archive lookup adds 0x4000, like every
 * other static). `0x2073` is the default in-window pointing-gauntlet cursor: it is
 * `_cursorData[war, 9]` in ClassicUO's `Game/GameCursor.cs` cursor table (rows
 * `0x206A..0x2079`; index 9 is the centre/default the client shows when the mouse
 * is not near a screen edge and not in war mode).
 *
 * **The directional ids and the id->`Direction` mapping are a VERIFY-flagged
 * hypothesis.** ClassicUO draws the in-window cursor from `_cursorData[war, d]`
 * where `d` is the 0..7 index `GameCursor.GetMouseDirection` returns - the same
 * order as ClassicUO's `Direction` enum (`North=0, Right(NE)=1, East=2, Down(SE)=3,
 * South=4, Left(SW)=5, West=6, Up(NW)=7`), which is exactly this codebase's
 * {@link Direction} order. So the natural mapping of the peace-row indices `0..7`
 * (`0x206A..0x2071`, the ids immediately below the idle `0x2073`) onto our headings
 * is:
 *
 *   0x206A -> N    0x206B -> NE   0x206C -> E    0x206D -> SE
 *   0x206E -> S    0x206F -> SW   0x2070 -> W    0x2071 -> NW
 *
 * Every directional `artId` is **VERIFY**-flagged: the id->heading mapping cannot
 * be confirmed against `_cursorData`/`GetMouseDirection` without a UO client on
 * this build host, though the committed bake of each id visibly points its
 * declared heading. The per-direction `hotspot` values are **measured from the
 * committed baked art**: each is the fingertip - the extreme opaque pixel along
 * that cursor's heading - read off `assets/baked/cursor/<id>.webp` and stored at
 * native (pre-`CURSOR_SCALE`) resolution. A rotated gauntlet's fingertip sits in
 * a different corner per heading, so sharing the idle `(3,1)` would put the click
 * point at the image's top-left for every direction (the original #66 bug).
 */
/**
 * A **blood-splatter** art tile (Blood splatter, PRD #372). `id` is the friendly
 * name used in the baked asset filename (`assets/baked/blood/blood.webp`, one shared
 * atlas keyed by these names) and the runtime glob; `artId` is the raw UO **static**
 * art id the converter decodes (the archive lookup adds the 0x4000 offset, exactly
 * like {@link StaticDefinition}/{@link CursorDefinition}). These 7 graphics are the
 * plain single-frame item statics ServUO spawns on a meaty hit
 * (`Scripts/Items/Decorative/Blood.cs`) - they carry **no AnimData entry**, so they
 * are baked on the single-frame {@link decodeStaticTile} precedent (the
 * `statics`/`cursors` recipe), **not** the AnimData-chained `effects` bake.
 */
export interface BloodDefinition extends Definition {
  artId: number;
}

/**
 * The 7 blood-splatter statics - **0x1645 and 0x122A..0x122F** - the client draws as
 * a transient ground decal on a landed hit (PRD #372). Both the real converter
 * (`bakeBlood`) and the placeholder stub (`stubBlood`) walk this registry, so the
 * baked blood set can never drift from what the runtime globs
 * (`src/render/bloodArt.ts`). The array **order is the wire "art index" 0..6** the
 * splat scatter rolls (Slice B rolls it server-side; Slice A's dev hook rolls it
 * locally), so the renderer maps `art` -> `bloodArt.all()[art]` and, absent a baked
 * texture, to a Graphics-primitive fallback varied by that same index.
 *
 * VERIFY: the 7 ids are the ServUO `Blood.cs` `m_Images` set, confirmed against
 * source but not against a decoded UO client on this host - the captain's
 * `npm run convert` confirms each decodes to real art (a warn-skip degrades that id
 * to the Graphics fallback, never a fatal bake).
 */
export const bloodArt = new Registry<BloodDefinition>('blood', [
  { id: 'blood-0', artId: 0x1645 /* VERIFY */ },
  { id: 'blood-1', artId: 0x122a /* VERIFY */ },
  { id: 'blood-2', artId: 0x122b /* VERIFY */ },
  { id: 'blood-3', artId: 0x122c /* VERIFY */ },
  { id: 'blood-4', artId: 0x122d /* VERIFY */ },
  { id: 'blood-5', artId: 0x122e /* VERIFY */ },
  { id: 'blood-6', artId: 0x122f /* VERIFY */ },
]);

/** How many blood art tiles exist - the modulus a scatter roll picks an art index within. */
export const BLOOD_ART_COUNT = 7;

export const cursors = new Registry<CursorDefinition>('cursor', [
  { id: 'gauntlet', artId: 0x2073 /* VERIFY: ClassicUO GameCursor _cursorData[war,9] */, hotspot: { x: 3, y: 1 }, dir: null },
  { id: 'gauntlet-n', artId: 0x206a /* VERIFY */, hotspot: { x: 7, y: 2 }, dir: Direction.N },
  { id: 'gauntlet-ne', artId: 0x206b /* VERIFY */, hotspot: { x: 29, y: 2 }, dir: Direction.NE },
  { id: 'gauntlet-e', artId: 0x206c /* VERIFY */, hotspot: { x: 42, y: 10 }, dir: Direction.E },
  { id: 'gauntlet-se', artId: 0x206d /* VERIFY */, hotspot: { x: 35, y: 24 }, dir: Direction.SE },
  { id: 'gauntlet-s', artId: 0x206e /* VERIFY */, hotspot: { x: 13, y: 32 }, dir: Direction.S },
  { id: 'gauntlet-sw', artId: 0x206f /* VERIFY */, hotspot: { x: 2, y: 27 }, dir: Direction.SW },
  { id: 'gauntlet-w', artId: 0x2070 /* VERIFY */, hotspot: { x: 2, y: 10 }, dir: Direction.W },
  { id: 'gauntlet-nw', artId: 0x2071 /* VERIFY */, hotspot: { x: 2, y: 2 }, dir: Direction.NW },
  // The duel target cursor (Player duels, PRD #252, ADR-0139): UO cursor art
  // `0x2076` - the classic **gauntlet-holding-a-targeting-circle** shown while the
  // duel target cursor is armed (spoken "I wish to duel"), replacing the old
  // blue-tinted gauntlet (ADR-0130). It is a fixed, non-directional targeting
  // graphic (`dir: null`), never rotated with movement, so `gameCursor.ts` paints
  // it whole for the whole 'duel' treatment. Both art paths bake it to
  // `cursor/duel-target.webp`: the clientless stub draws a gauntlet-with-reticle
  // recreation, `npm run convert` bakes the genuine `0x2076` over it.
  //
  // VERIFY: `artId` 0x2076 and the click hotspot are both captain-confirmed at bake
  // time. 0x2076 sits in ClassicUO's `_cursorData` cursor rows (`0x206A..0x2079`,
  // see the gauntlet ids above); the hotspot is hypothesised at the targeting
  // circle's centre (where the aim lands), nudged to the real art's reticle when
  // `npm run convert` decodes 0x2076 against the captain's client.
  { id: 'duel-target', artId: 0x2076 /* VERIFY: ClassicUO cursor-row targeting gauntlet-with-circle */, hotspot: { x: 16, y: 16 } /* VERIFY: reticle centre */, dir: null },
]);

/**
 * The sound effects the client plays as a per-viewer reaction to the
 * authoritative snapshot stream (footsteps on a mobile's step, a door's
 * open/close toggle) - the audio sibling of roof-hiding and overhead
 * line-of-sight: a render-time decision per client, never on the wire (no
 * protocol/sim change). The model is ClassicUO's, not its code (see ADR-0092):
 *
 *  - **Footsteps** are client-generated, like ClassicUO's
 *    `Mobile.ProcessFootstepsSound`: a human's step plays an *alternating* `-a`/`-b`
 *    pair, gated to one sound per ~520ms per mobile so a run doesn't machine-gun.
 *    We use the **exact sounds ClassicUO hardcodes**: on-foot step id `0x012B` =
 *    `feet12a` and `0x012C` = `feet12b` (confirmed against the captain's UOFiddler).
 *    UO plays this same pair everywhere - it does *not* pick the footstep by terrain
 *    - so it is correct on the bank's cobblestone as on any other ground (a separate,
 *    later material-named set `feet_pvmt`/`grvl`/`sand`/... exists at id 0x0339+ but
 *    the live client never selects from it). Retheme by swapping the `file` here - it
 *    is pure data - but `feet12a/b` is the faithful default.
 *  - **Doors** play the standard wooden open/close pair a RunUO `BaseDoor` emits:
 *    open `0xEA` (`open01a`), close `0xF1` (`shut03a`) - the bank's front doors are
 *    wooden.
 *
 * Both the real converter (copies the wav) and the placeholder stub (writes
 * silence) walk this registry, so the baked sound set can never drift from what the
 * runtime loads. The numeric UO sound id each `file` corresponds to is recorded
 * inline for provenance.
 */
export const sounds = new Registry<SoundDefinition>('sound', [
  { id: 'footstep-a', file: 'feet12a' }, // UO sound 0x012B (ClassicUO's on-foot step)
  { id: 'footstep-b', file: 'feet12b' }, // UO sound 0x012C (alternate)
  { id: 'door-open', file: 'open01a' }, // UO sound 0xEA
  { id: 'door-close', file: 'shut03a' }, // UO sound 0xF1
  { id: 'level-up', file: 'm_lock' }, // UO sound 0x1F4 (cosmetic-award level-up cue, PRD #101)
  { id: 'sword7', file: 'sword7' }, // UO Kryss melee hit sound ("bank" easter-egg cue, ADR-0078)
  // Card-duel boundary cue: the opening drum warns both participants as soon as
  // any PvP, Duel Board, or practice match opens. The converter copies the
  // captain-provided `drum_battle.wav`; clientless hosts receive the usual silent stub.
  { id: 'card-duel-start', file: 'drum_battle' },
  // Owner-only turn prompt for card battles. The converter copies the literal
  // `m_clear_throat.wav` recording from UO_SOUNDS_DIR; CI receives a silent stub.
  { id: 'card-your-turn', file: 'm_clear_throat' },
  // Rat Swarm combat cues: a random male/female human "oomph" grunt on the local
  // player's stomp swing (picked by the player's body sex), and a random rat squeal
  // on any rat's death clip. All from the same UO wav pack the other sounds copy from.
  { id: 'ugh-m-1', file: 'm_oomph_01' }, // male stomp grunt (randomized set)
  { id: 'ugh-m-2', file: 'm_oomph_06' },
  { id: 'ugh-m-3', file: 'm_oomph_09' },
  { id: 'ugh-m-4', file: 'moomph03' },
  { id: 'ugh-m-5', file: 'moomph05' },
  { id: 'ugh-f-1', file: 'f_oomph_01' }, // female stomp grunt (randomized set)
  { id: 'ugh-f-2', file: 'f_oomph_02' },
  { id: 'ugh-f-3', file: 'f_oomph_03' },
  { id: 'rat-die-1', file: 'mngbat3' }, // rat death squeal (randomized set)
  { id: 'rat-die-2', file: 'mngbat4' },
  { id: 'rat-die-3', file: 'mngbat5' },
  // Rat Swarm event-boundary cues, played off the RatSwarmStatus active<->inactive
  // transition (see swarmCue): a scream when the swarm floods in, a "protect the
  // bank" horn when it's over.
  { id: 'swarm-start', file: 'm_scream' }, // event start klaxon
  { id: 'swarm-over', file: 'protect' }, // event over cue
  // Duel combat cues (Player duels, PRD #252, ADR-0131), gendered where the sound
  // is a body's own voice: a random get-hit grunt on a landed blow (by the
  // victim's body sex), a shared whiff on a miss, a random death cry on the
  // killing blow (by the loser's sex), and one shared resurrection chime.
  // Basenames captain-confirmed (ADR-0142): the hit grunts are the "oomph" effort
  // set (reused from the rat-stomp grunts, `*_oomph_0N`), the death cries are
  // `*_death_0N`, and the resurrection is `resurect` (that exact spelling - single
  // 'r', no digits). Copied by `copySounds` from the captain's UO wav pack.
  { id: 'hit-m-1', file: 'm_oomph_01' }, // male get-hit grunt (randomized set)
  { id: 'hit-m-2', file: 'm_oomph_02' },
  { id: 'hit-m-3', file: 'm_oomph_03' },
  { id: 'hit-f-1', file: 'f_oomph_01' }, // female get-hit grunt (randomized set)
  { id: 'hit-f-2', file: 'f_oomph_02' },
  { id: 'hit-f-3', file: 'f_oomph_03' },
  { id: 'whiff-1', file: 'swish01' }, // shared miss whiff (randomized set)
  { id: 'whiff-2', file: 'swish02' },
  { id: 'whiff-3', file: 'swish03' },
  { id: 'death-m-1', file: 'm_death_01' }, // male death cry (randomized set)
  { id: 'death-m-2', file: 'm_death_02' },
  { id: 'death-f-1', file: 'f_death_01' }, // female death cry (randomized set)
  { id: 'death-f-2', file: 'f_death_02' },
  { id: 'resurrect', file: 'resurect' }, // shared resurrection chime (captain spelling: 'resurect')
  // Blackthorn's Storm event cues (PRD #289 Slice B, ADR-0148). `storm-start` is
  // Blackthorn's spawn growl - the event-start signal played when the storm begins;
  // the `vortex-attack-N` set is a randomized wind gust played on an energy vortex's
  // swing. The mass-resurrection at the event's end reuses the existing `resurrect`
  // entry above, so it needs no new registration. Basenames captain-provided; VERIFY at bake.
  { id: 'storm-start', file: 'm_growl' }, // Blackthorn spawn growl / event-start signal (VERIFY)
  { id: 'vortex-attack-1', file: 'wind01_l' }, // vortex swing gust (randomized set) (VERIFY)
  { id: 'vortex-attack-2', file: 'wind02_l' },
  { id: 'vortex-attack-3', file: 'wind03_l' },
  // Chaos-llama vortex variant cues (ADR-0162): the four UO llama barks, played on a
  // llama's movement AND its attack in place of the wind gust (the `vortexCreatureCue`
  // set-by-body pick). Note the pack deliberately SKIPS `llama3` - exactly `llama1`,
  // `llama2`, `llama4`, `llama5`. Copied by `copySounds` from the captain's UO wav pack;
  // the clientless stub writes a silent placeholder per id, so dev/CI wire and run.
  // The source basenames are the raw `llamaN` names; VERIFY they exist in the pack at bake.
  { id: 'llama-1', file: 'llama1' },
  { id: 'llama-2', file: 'llama2' },
  { id: 'llama-4', file: 'llama4' },
  { id: 'llama-5', file: 'llama5' },
  // The arena fire-mage spell FX sounds (ADR-0187): one per baked {@link effects}
  // effect, played at the volley's own flight-launch / impact instants (the
  // VolleyFxHooks seam). The ids are the ServUO spell PlaySound calls - Magic Arrow
  // 0x1E5, Fireball 0x15E (the AOS effect), Explosion 0x307, Flame Strike 0x208 -
  // and the basenames are those IDs' embedded sound.mul names. VERIFY at bake.
  { id: 'spell-magic-arrow', file: 'm_arrow' }, // UO sound 0x1E5
  // The arena archer's shot cues (PRD #446 Slice 1, ADR-0237): the bow twang
  // (UO sound 0x234) and the crossbow release (0x2B1), sounded client-side off
  // the arrow/bolt flight FX (the spell-cue path). Basenames guessed from the UO
  // ranged-weapon samples - VERIFY at the captain's bake; a missing wav degrades
  // to silence like every cue.
  { id: 'bow-shot', file: 'arrow1' }, // UO sound 0x234 - bow release (VERIFY basename)
  { id: 'crossbow-shot', file: 'xbowloos' }, // UO sound 0x2B1 - crossbow release (VERIFY basename)
  // The arrow/bolt IMPACT thwack (ADR-0237) - played on the struck enemy when a
  // physical shot lands (the `onHit` viaEffect seam), on top of its hurt grunt.
  // One sound covers arrow and bolt. VERIFY basename at the captain's bake.
  { id: 'arrow-hit', file: 'thud1' }, // UO ranged hit thud ~0x233 (VERIFY basename)
  { id: 'spell-fireball', file: 'fyrbal01' }, // UO sound 0x15E
  { id: 'spell-explosion', file: 'exp03' }, // UO sound 0x307
  { id: 'spell-flame-strike', file: 'flamstrk' }, // UO sound 0x208
  { id: 'spell-poison', file: 'poison' }, // UO sound 0x205
  { id: 'spell-lightning', file: 'thundr02' }, // UO sound 0x029
  { id: 'spell-greater-heal', file: 'gratheal' }, // UO sound 0x202
  { id: 'spell-cure', file: 'cure' }, // UO sound 0x1E0
  { id: 'warrior-bless', file: 'bless' },
  // Sphere Duel's visible fizzle emote is paired with the classic UO spell-fail
  // cue (ServUO Spell.DoFizzle, sound 0x5c). The client archive's entry 0x5c is
  // literally the alligator recording "aligatr3.wav" - UO's famous fizzle blub
  // doubles as the alligator attack grunt (verified against soundLegacyMUL.uop).
  { id: 'sphere-fizzle', file: 'aligatr3' }, // UO sound 0x05c
  // The moongate cues (PRD #362 Slice A): the gate-summon hum ServUO's Gate Travel
  // spell plays when a gate opens (UO sound 0x20E), and the teleport whoosh
  // `Moongate.OnGateUsed` plays on the traveller (0x1FE). Both derived client-side
  // from the snapshot (a moongate appearing / a nearby mobile's teleport jump - see
  // SoundObserver), never a wire message. VERIFY basenames at bake.
  { id: 'moongate-open', file: 'gatetrvl' }, // UO sound 0x20E (VERIFY)
  { id: 'moongate-enter', file: 'teleport' }, // UO sound 0x1FE (VERIFY)
  // The King's Justice cues (PRD #361, ADR-0194): the smoke-poof teleport sound the
  // guard ritual plays on every poof-in/out (ServUO 0x1FE - the same `teleport`
  // basename the moongate-enter whoosh uses, a nice cross-check), and the halberd's
  // polearm hit on the wack (ServUO BasePoleArm HitSound 0x237). Both basenames
  // resolved from a real client's sound.mul embedded names (anchored against the
  // known rows above: 0x12B=feet12a, 0xEA=open01a, 0x23C=sword7); VERIFY at bake.
  { id: 'poof', file: 'teleport' }, // UO sound 0x1FE (smoke poof / teleport)
  { id: 'halberd-hit', file: 'hvyswrd4' }, // UO sound 0x237 (polearm hit)
  // The arena XP-gem absorb plink (VS-style XP orbs): sounded client-side as a
  // magnetized orb cluster finishes its suck-in. The committed wav is a
  // synthesized bell plink (a placeholder that already sounds right); the UO
  // source is the gem-drop sample - a captain bake replaces it. VERIFY at bake.
  { id: 'xp-pickup', file: 'dropgem1' }, // UO sound 0x2E5-ish gem drop (VERIFY)
  // Favorite-outfit slot purchase confirmation: the existing positive collection
  // cue from the UO sound pack. The registry-driven stub keeps dev and CI graceful.
  { id: 'collection-positive', file: 'collection_positive' },
  // The arena Vacuum grab whoosh (PRD #431 Slice C): one full-volume cue as the
  // runner claims a vacuum and the whole gem field inrushes. The committed wav is
  // a synthesized whoosh (the xp-pickup precedent - a placeholder that already
  // sounds right, generated offline); the UO source mapped here is a wind/gust
  // sample for a captain bake to replace it with. VERIFY at bake.
  { id: 'vacuum-grab', file: 'gust1' }, // UO wind gust ~0x14F (VERIFY)
  // The arena level-up fanfare's cap note (ADR-0218): UO sound 0x100, embedded
  // name `sfx15.wav` - CONFIRMED via the ruins-riches soundidx.mul/sound.mul
  // parse (anchored on feet12a 0x12B / open01a 0xEA / sword7 0x23C / m_lock
  // 0x1F4), and the committed bake was extracted from that same client data,
  // so no captain re-bake is needed (a convert run just re-copies it).
  { id: 'level-up-fanfare', file: 'sfx15' },
  // The arena low-HP heartbeat (PRD #430 Slice D, ADR-0227): the throttled lub-dub
  // while the runner is under the danger threshold. The committed wav is a
  // synthesized heartbeat (the xp-pickup precedent - a placeholder that already
  // sounds right, generated offline); sound.mul has no true heartbeat sample, so
  // the UO source mapped here is the nearest low drum thump for a captain bake to
  // replace it with. VERIFY at bake.
  { id: 'heartbeat', file: 'drum2' }, // UO drum thump 0x39-ish (VERIFY)
  // The bestiary monster cues (ADR-0187): four `mon-<family>-<role>` rows per sound
  // family (move/attack/hurt/die), generated from the family x role table beside the
  // {@link monsters} registry so the baked set can never drift from the bestiary.
  // Basenames are the embedded sound.mul names at ServUO's BaseSoundID offsets;
  // VERIFY each exists in the captain's pack at bake.
  ...monsterSoundEntries(),
]);

/**
 * The background-music tracks the client streams through the in-browser General-MIDI
 * synthesizer (ADR-0146). Each entry names a UO `.mid` track by friendly `id` and its
 * source basename; `npm run convert` copies `<file>.mid` from `UO_MIDI_DIR` into
 * `assets/baked/music/<id>.mid`, and the clientless stub writes a short silent `.mid`
 * placeholder per id, so the baked music set is registry-driven and can never drift
 * from what the runtime loads - the exact discipline the {@link sounds} registry uses
 * for wavs. The `.mid` files are copyrighted UO material, so - unlike the freely
 * redistributable soundfont (`assets/soundfont/`) - they are gitignored and baked
 * locally, never committed.
 *
 * These are the five classic Ultima/Britannia themes the login playlist cycles, in
 * this exact order (see {@link LOGIN_PLAYLIST}); id === basename because the UO track
 * filenames are already friendly. Only the tracks the playlist actually uses are
 * registered - add a row here (and, if it should play, to {@link LOGIN_PLAYLIST}) to
 * bake and stream another.
 *
 * `combat1`, `dungeon2`, and the {@link ARENA_PLAYLIST} tracks are the odd ones out
 * (ADR-0146 duel-combat override; PRD #289 storm override; Arena run override,
 * ADR-0177/0210): none is in the login playlist, but all are registered so they
 * bake/stub like the rest, because the {@link ../audio/musicPlayer.MusicPlayer} swaps to
 * one under an override and reverts to the login playlist when it lifts - `combat1`
 * while the *local* player is in a live duel, `dungeon2` while a Blackthorn's Storm is
 * running, the rotating arena playlist while the local player's Arena run is live. All
 * are client-local (the storm one keyed on the replicated StormStatus edge, the duel one
 * on the player's own combat stance, the arena one on the owner-only ArenaRunStatus
 * active edge) - no networked music. Precedence: arena over storm over duel (each pair
 * mutually exclusive live; belt-and-suspenders).
 */
export const music = new Registry<MusicDefinition>('music', [
  { id: 'oldult06', file: 'oldult06' }, // Stones (Ultima theme)
  { id: 'oldult05', file: 'oldult05' },
  { id: 'oldult03', file: 'oldult03' },
  { id: 'britain1', file: 'britain1' }, // Britain / Britannia town themes
  { id: 'britain2', file: 'britain2' },
  { id: 'combat1', file: 'combat1' }, // Duel-combat override + Arena playlist (not in LOGIN_PLAYLIST)
  { id: 'dungeon2', file: 'dungeon2' }, // Blackthorn's Storm override (not in LOGIN_PLAYLIST)
  // The rest of the Arena battle playlist (ADR-0210): the run rotates through
  // these instead of looping one track. All UO battle/dungeon themes, baked from
  // UO_MIDI_DIR and silent-stubbed for dev/CI like every other track.
  { id: 'combat2', file: 'combat2' },
  { id: 'btcastle', file: 'btcastle' },
  { id: 'dungeon9', file: 'dungeon9' },
  { id: 'samlethe', file: 'samlethe' },
  { id: 'serpents', file: 'serpents' },
  // The one-shot new-high-score victory sting (ADR-0210), played via MusicPlayer.
  { id: 'victory', file: 'victory' },
]);

/**
 * The login background-music playlist (ADR-0146): the exact order the tracks play on
 * entering the world, looping back to the first after the last plays to completion
 * (`oldult06 -> oldult05 -> oldult03 -> britain1 -> britain2 -> oldult06 -> ...`). The
 * pure {@link ../audio/playlist.Playlist} advances through this list and wraps at the
 * end; the {@link ../audio/musicPlayer.MusicPlayer} loads and plays each track's baked
 * `.mid` in turn off the synth's `songEnded` event. Every id here must be a registered
 * {@link music} track so a baked (or stubbed) `.mid` exists for it.
 */
export const LOGIN_PLAYLIST: readonly string[] = [
  'oldult06',
  'oldult05',
  'oldult03',
  'britain1',
  'britain2',
];

/**
 * The **Arena battle song pool** (ADR-0210/0232): each Arena run randomly picks
 * **one** song from this list at engage and sticks to it for the whole run -
 * thematic per round, different round to round. The
 * {@link ../audio/musicDirector.MusicDirector}'s arena override opens on the
 * song's base `.mid` and climbs through its `.mp3` intensity remixes as the horde
 * escalates (each version looping until the next tier-up). Every id is a
 * registered {@link music} track (baked or silent-stubbed), and every track
 * carries {@link ARENA_INTENSITY_TIERS} intensity versions - the base `.mid` plus
 * escalating `.mp3` remixes (see {@link arenaRemixDefs}).
 */
export const ARENA_PLAYLIST: readonly string[] = [
  'combat1',
  'combat2',
  'btcastle',
  'dungeon9',
  'samlethe',
  'serpents',
];

/**
 * How many **intensity tiers** each Arena battle track has: tier 1 is the base
 * baked `.mid` (synthesized like every other track), tiers 2..5 are the captain's
 * pre-rendered `.mp3` remixes of the same song at rising intensity, baked beside the
 * mid as `<id>_<tier>.mp3` (source `<file>_<tier>.mp3` in `UO_MIDI_DIR`). A run
 * always opens on the mid and ramps up through the remixes as the horde escalates -
 * see {@link ../audio/musicDirector.arenaMusicTier} for the ramp clock.
 */
export const ARENA_INTENSITY_TIERS = 5;

/**
 * The track id the arena override plays for `base` at intensity `tier`: the base id
 * itself at tier 1 (the `.mid`), `<base>_<tier>` (the `.mp3` remix) above it. Pure
 * naming only - availability/degrade decisions live in the MusicDirector.
 */
export function arenaRemixTrackId(base: string, tier: number): string {
  return tier <= 1 ? base : `${base}_${tier}`;
}

/**
 * Every Arena intensity remix as an `{ id, file }` pair - the same shape as a
 * {@link music} registry row, derived rather than registered because the set is
 * exactly {@link ARENA_PLAYLIST} x tiers 2..{@link ARENA_INTENSITY_TIERS}: `id` is
 * the runtime/baked stem (`combat1_2`), `file` the source basename in the captain's
 * MIDI pack (`<registry file>_2`, i.e. `combat1_2.mp3`). The bake, the stub, and the
 * runtime loader all walk this one list so the remix set can never drift.
 */
export function arenaRemixDefs(): MusicDefinition[] {
  const defs: MusicDefinition[] = [];
  for (const base of ARENA_PLAYLIST) {
    const row = music.all().find((m) => m.id === base);
    if (!row) continue; // ARENA_PLAYLIST ⊆ registry is pinned by tests
    for (let tier = 2; tier <= ARENA_INTENSITY_TIERS; tier++) {
      defs.push({ id: arenaRemixTrackId(base, tier), file: `${row.file}_${tier}` });
    }
  }
  return defs;
}

/**
 * The UO effect animations the game bakes (ADR-0180) - groundwork for the arena
 * fire-mage FX (a later render slice consumes them; nothing plays these yet). Each
 * `artId` is the effect's first static art frame; the converter reads the rest of
 * the strip from `AnimData.mul`. The ids are the classic ClassicUO/RunUO effect
 * graphics: `0x36E4` Magic Arrow, `0x36D4` Fireball, `0x36B0` Explosion, `0x3709`
 * Flame Strike. VERIFY: all four ids (and their AnimData strips) are confirmed
 * against real client data when the captain runs `npm run convert` - a wrong id is
 * a loud decode failure there; the stub pipeline covers dev/CI until then.
 */
export const effects = new Registry<EffectDefinition>('effect', [
  { id: 'magic-arrow', artId: 0x36e4 }, // ServUO Magic Arrow moving-particle art.
  { id: 'fireball', artId: 0x36d4 }, // large fireball projectile. VERIFY
  { id: 'explosion', artId: 0x36b0 }, // explosion one-shot. VERIFY
  { id: 'flame-strike', artId: 0x3709 }, // flame strike fire column one-shot. VERIFY
  { id: 'poison', artId: 0x374a }, // Poison target particles, ServUO 0x374A.
  {
    id: 'lightning',
    artId: 0x4e20,
    frameArtIds: Array.from({ length: 10 }, (_, index) => 0x4e20 + index),
    frameIntervalMs: 40,
    gumpArt: true,
    anchor: 'bottom',
    additive: true,
  },
  { id: 'greater-heal', artId: 0x376a }, // Greater Heal target sparkles, ServUO 0x376A.
  { id: 'cure', artId: 0x373a }, // Cure target sparkles, ServUO 0x373A.
  {
    id: 'warrior-aegis-sparkle',
    artId: 0x3763,
    frameArtIds: Array.from({ length: 16 }, (_, index) => 0x3763 + index),
    frameIntervalMs: 60,
    anchor: 'center',
    additive: true,
  },
  // The moongate (PRD #362 Slice A, the Wandering Moongate): the looping blue gate
  // art (ServUO `Moongate`'s default ItemID 0x0F6C, animated via its AnimData strip)
  // and the one-shot summon flash (0x1AF3) played at the spawn tile before the gate
  // settles into its loop. Baked on the effects path like the spell FX. VERIFY both
  // ids at the captain's `npm run convert`; the stub pipeline covers dev/CI.
  { id: 'moongate', artId: 0x0f6c }, // looping blue moongate. VERIFY
  { id: 'moongate-flash', artId: 0x1af3 }, // one-shot summon flash. VERIFY
  { id: 'poof', artId: 0x3728 }, // smoke poof one-shot (the ServUO guard ritual, PRD #361). VERIFY
  // The Arena draft-expansion FX shelf (PRD #378): keystone / ground-zone /
  // telegraph art. All VERIFY at the captain's bake; all degrade to Graphics
  // primitives when unbaked (the ADR-0180 contract).
  { id: 'fire-snake', artId: 0x36f4 }, // piercing fire-serpent projectile (Fire Serpent keystone). VERIFY
  { id: 'big-explosion', artId: 0x36c0 }, // large explosion one-shot (Cataclysm / meteor impact). VERIFY
  { id: 'fire-field-ew', artId: 0x398c }, // looping east-west fire-field tile. VERIFY
  { id: 'fire-field-ns', artId: 0x3998 }, // looping north-south fire-field tile. VERIFY
  { id: 'star', artId: 0x37c4 }, // the falling star (Meteor keystone flight). VERIFY
  { id: 'sparkle', artId: 0x373a }, // looping sparkle (delayed-hit telegraph grammar). VERIFY
  { id: 'death-vortex', artId: 0x3798 }, // vortex swirl one-shot (Flame Vortex summon flash); AnimData strip head 0x3798, frames 0x3799..0x379d then 0x3798
  // The Combustion mushroom cloud (PRD #419 slice C): the fire-mushroom
  // explosion one-shot a burning enemy's corpse detonates with. Its art static
  // 0xB229 is the FIRST effect id above the classic 14-bit (0x4000) static-art
  // range, so it exercises the AnimData bake path in higher-id territory - the
  // converter's MAX_ART_ID ceiling was lifted to the full 16-bit static item-id
  // space to cover it. A client-pack still missing the art warn-skips and ships
  // the Graphics degrade (the baked-statics-atlas convention).
  { id: 'fire-mushroom', artId: 0xb229 }, // mushroom-cloud one-shot (Combustion keystone), AnimData strip head
  // The archer (PRD #446 Slice 1, ADR-0237). The flying arrow / bolt are ITEM
  // art (single static tile, `itemArt: true`), rotated client-side along the
  // flight vector; the poison-field pair are ordinary AnimData strips like the
  // fire fields. All VERIFY at the captain's bake; all degrade to Graphics.
  { id: 'arrow', artId: 0x0f42, itemArt: true }, // flying arrow (item art). VERIFY
  { id: 'bolt', artId: 0x1bfe, itemArt: true }, // flying crossbow bolt (item art). VERIFY
  { id: 'poison-field-ew', artId: 0x3915 }, // looping east-west poison-field tile. VERIFY
  { id: 'poison-field-ns', artId: 0x3920 }, // looping north-south poison-field tile. VERIFY
]);

/** The declared effect ids, as a type - keeps consumers exhaustive at compile time. */
export type EffectId =
  | 'magic-arrow'
  | 'fireball'
  | 'explosion'
  | 'flame-strike'
  | 'poison'
  | 'lightning'
  | 'greater-heal'
  | 'cure'
  | 'warrior-aegis-sparkle'
  | 'moongate'
  | 'moongate-flash'
  | 'poof'
  | 'fire-snake'
  | 'big-explosion'
  | 'fire-field-ew'
  | 'fire-field-ns'
  | 'star'
  | 'sparkle'
  | 'death-vortex'
  | 'fire-mushroom'
  | 'arrow'
  | 'bolt'
  | 'poison-field-ew'
  | 'poison-field-ns';
/** The effects that travel point-to-point (a later slice lerps them along a path). */
export type ProjectileEffectId =
  | 'magic-arrow'
  | 'fireball'
  | 'fire-snake'
  | 'star'
  | 'arrow'
  | 'bolt';
/** The effects that play once in place at an impact/target tile. */
export type ImpactEffectId =
  | 'explosion'
  | 'flame-strike'
  | 'poison'
  | 'lightning'
  | 'greater-heal'
  | 'cure'
  | 'poof'
  | 'big-explosion'
  | 'death-vortex'
  | 'fire-mushroom';
/** The effects that loop in place on the ground for a caller-chosen duration
 * (fire fields, the sparkle telegraph - the PRD #378 zone/telegraph grammar).
 * `molten` (PRD #419 slice B) is the burning-ground **residue** skin - the
 * Scorched Earth trail and Meteor impact pools - and is the one ground look
 * NOT backed by an {@link effects} registry strip: the client draws it from
 * the {@link bloodArt} statics tinted fire (~0xFF8030), so it is deliberately
 * absent from {@link EffectId}. Fire-field strips stay the **wall** grammar
 * (Cataclysm ground). */
export type GroundEffectId =
  | 'fire-field-ew'
  | 'fire-field-ns'
  | 'poison-field-ew'
  | 'poison-field-ns'
  | 'sparkle'
  | 'molten'
  // The archer's Caltrops slow tile (ADR-0237): a ground marker the client loops
  // for the tile's lifetime. Drawn from the poison/Graphics degrade, not its own
  // strip (a caltrop has no UO effect art) - a distinct GroundEffectId so the
  // planner/overlay can tint it apart from a poison cloud.
  | 'caltrop';

/**
 * The **people** (human, bodies 400+) actions the game uses at runtime, and
 * therefore the actions the converter bakes for every people-class body and
 * every equipment layer: 0 = walk, 2 = run, 4 = stand (resting pose), 5 = fidget
 * (idle one-shot), and 32 = attack, the "bow lesser" swing the stomper plays on a
 * rat kill (the first combat clip, ADR-0116, retiring ADR-0001's locomotion-only
 * stance).
 *
 * The **duel clips** (PRD #252 Duel slice B, ADR-0129) extend the same set: the
 * combat-stance idle and advance the duel swaps a fighter's stand/walk to, three
 * punch swings a landed hit plays at random, a get-hit flinch, and two directional
 * deaths. All are baked for bodies 400/401 **and every equipment layer** (the
 * people layout the equipment anim graphics share), loaded as **optional** clips
 * that degrade to the stand pose when the art is absent, and played once on a
 * pooled sprite through the same `ActionAnimation` broadcast the stomp swing uses
 * (`PlayerSprite.playAction`). The friendly `id` is the **wire clip name** the
 * combat/death logic slices broadcast (an open string on the wire, ADR-0116), so
 * these names are a cross-slice contract; the UO anim-group `action` numbers are
 * the UOFiddler `CombatIdle1H_01` (7) / `CombatAdvance_1H_01` (15) / punches
 * (9/10/11) / get-hit (20) / die-forward (21) / die-back (22). **Every duel index is
 * now confirmed against real ClassicUO** (the scout study, ADR-0142): stand 4,
 * combat-idle 7, punch 9/10/11, advance 15, hit 20, die 21/22 - the VERIFY flags are
 * cleared. (The stomp `attack` 32 is a separate ADR-0116 rat-swarm clip, still VERIFY.)
 */
export const actions = new Registry<ActionDefinition>('action', [
  { id: 'walk', action: 0 },
  { id: 'run', action: 2 },
  // The ARMED locomotion gait (UO people anim groups Walk_Armed 1 / Run_Armed 3).
  // Holding a one-handed item makes the character walk/run with the arm carrying it,
  // and - crucially for held light sources (torch/candle/lantern, ADR-0063) - the
  // per-frame hand-bounce is baked into these frames where the unarmed groups 0/2
  // hold the light dead still (a held-still placeholder). ClassicUO switches to the
  // armed group when something is in hand (`GetGroupForAnimation`); the renderer does
  // the same for the accessory slot (ADR-0277), so the light finally bounces in hand.
  { id: 'walk-armed', action: 1 }, // Walk_Armed
  { id: 'run-armed', action: 3 }, // Run_Armed
  { id: 'stand', action: 4 },
  { id: 'fidget', action: 5 },
  { id: 'attack', action: 32 }, // Bow_Lesser_01 - the stomp swing (ADR-0116). VERIFY at bake.
  // The arena skill cast (ADR-0187): the Spell1 one-hand cast swing, baked under its
  // own wire clip name so the volley carries no stomp grunt (the client's swing-grunt
  // cue keys on the `attack` clip name - the `bow` no-sound precedent). A placeholder
  // stub drops to the stand pose on both render paths ({@link CAST_CLIP_IDS}).
  { id: 'cast', action: 16 }, // Spell1 - the arena skill cast (ADR-0187). VERIFY at bake.
  { id: 'spell2', action: 17 }, // Spell2 - the Warrior Aegis cast.
  // The arena archer's shots (PRD #446 Slice 1, ADR-0237): the ClassicUO people
  // anim groups AttackBow (18) and AttackCrossbow (19) - confirmed against
  // `ClassicUO.Assets/AnimationsLoader.cs` `PeopleAnimationGroup`. The archer
  // plays `shoot-bow` per volley, swapping to `shoot-crossbow` under Heavy Draw
  // (server-chosen). Like `cast`, one-shots with no persistent stance, so a
  // placeholder stub drops to the stand pose on both render paths
  // ({@link SHOOT_CLIP_IDS}). VERIFY at bake.
  { id: 'shoot-bow', action: 18 }, // AttackBow - the archer's bow shot. VERIFY at bake.
  { id: 'shoot-crossbow', action: 19 }, // AttackCrossbow - the Heavy Draw bolt. VERIFY at bake.
  // Player emotes (ADR-0144): the two UO social gestures a player triggers by hotkey
  // (Ctrl+B / Ctrl+S). `bow` is UO anim action 32 (Bow) - the same "bow lesser" clip the
  // stomp `attack` reuses, baked under its own name so the emote carries no combat sound;
  // `salute` is action 33 (Salute). Both are optional one-shot clips: a placeholder stub
  // degrades to the stand pose (dropped, never floated/squared - see {@link EMOTE_CLIP_IDS}
  // and {@link ../render/playerArt.isPlaceholderEmoteClip}), so an unbaked host shows a
  // clean stand until the captain bakes the real gesture. VERIFY at bake.
  { id: 'bow', action: 32 }, // Bow (UO anim group 32) - the /bow emote. VERIFY at bake.
  { id: 'salute', action: 33 }, // Salute (UO anim group 33) - the /salute emote. VERIFY at bake.
  // The dance emote (ADR-0387): UO anim action 30, triggered by typing the classic
  // `*dances*` action line rather than a hotkey. Unlike the one-shot bow/salute it is
  // rebroadcast with a repeat count ({@link DANCE_REPEAT}), so the clip loops a few
  // times and the dance reads as a dance. Degrades exactly like the other emotes (a
  // placeholder stub drops to the stand pose on both render paths). VERIFY at bake.
  { id: 'dances', action: 30 }, // Dance (UO anim group 30) - the *dances* emote. VERIFY at bake.
  // The two-handed slash (The King's Justice guard's halberd wack, PRD #361,
  // ADR-0194): UO people anim group 13 = AttackSlash2H_01, the halberd swing. A
  // one-shot with no persistent stance, so - exactly like the emote/cast one-shots -
  // a placeholder stub is dropped to the stand pose on both render paths
  // ({@link WEAPON_SWING_CLIP_IDS}). Fired only by the event sim, never
  // player-triggerable (deliberately NOT in EMOTE_CLIP_IDS). VERIFY at bake.
  { id: 'attack-2h', action: 13 }, // AttackSlash2H_01 - the halberd swing. VERIFY at bake.
  { id: 'thrust-2h', action: 14 }, // AttackPierce2H_01 - Piercing Thrust with a two-handed weapon. VERIFY at bake.
  { id: 'thrust-1h', action: 10 }, // AttackPierce1H_01 - Piercing Thrust with a one-handed weapon. VERIFY at bake.
  // Duel clips (ADR-0129) - people combat set; every index CONFIRMED vs ClassicUO (ADR-0142).
  { id: 'combat-idle', action: 7 }, // CombatIdle1H_01 - the duel combat stance.
  { id: 'combat-advance', action: 15 }, // CombatAdvance_1H_01 - combat-stance walk.
  { id: 'punch-1', action: 9 }, // punch swing (random pick).
  { id: 'punch-2', action: 10 }, // punch swing (random pick).
  { id: 'punch-3', action: 11 }, // punch swing (random pick).
  { id: 'hit', action: 20 }, // get-hit flinch on a landed blow.
  { id: 'die-forward', action: 21 }, // death, fall forward.
  { id: 'die-back', action: 22 }, // death, fall back.
  // The MOUNTED rider clips (ADR-0442): UO people anim groups 23 Horse_Walk_01,
  // 24 Horse_Run_01, 25 Horse_Idle_01 - the seated, legs-astride, reins-in-hand
  // poses a rider holds while a mount carries them. CONFIRMED against the captain's
  // client data: decoding body 400 groups 23/24/25 yields the seated posture, and
  // group 25 decodes exactly ONE frame per facing, the mounted-idle still pose UO
  // holds while a mount stands. They are ordinary people-table clips, so every
  // people body AND every equipment layer bakes them from this one registry - which
  // is what lets a mounted rider keep their hair, hat, and clothes.
  { id: 'ride-slow', action: 23 }, // Horse_Walk_01 - the rider's walking seat.
  { id: 'ride-fast', action: 24 }, // Horse_Run_01 - the rider's galloping seat.
  { id: 'ride-stand', action: 25 }, // Horse_Idle_01 - the rider's still seat.
]);

/**
 * The people-action ids added for player duels (ADR-0129): the combat stance
 * idle/advance, the three punch swings, the get-hit flinch, and the two deaths -
 * everything beyond the base locomotion (`walk`/`run`/`stand`/`fidget`) and the
 * ADR-0116 stomp `attack`. Equipment layers use this set to degrade a **placeholder**
 * duel-action clip to the stand pose (Duel deathfix squares bug, ADR-0142): a clothed
 * duelist's worn items must animate off real combat art where it exists, but never
 * render a leftover flat-colour stub box - see {@link ../render/playerArt.assembleClips}.
 */
export const DUEL_CLIP_IDS: readonly string[] = [
  'combat-idle',
  'combat-advance',
  'punch-1',
  'punch-2',
  'punch-3',
  'hit',
  'die-forward',
  'die-back',
];

/**
 * The subset of {@link DUEL_CLIP_IDS} that make up the persistent **war-mode combat
 * stance** a duelist holds for the whole fight (ADR-0140 re-fix): the combat idle
 * (action 7) shown while standing ready and the combat advance (action 15) shown while
 * closing on the opponent. Distinct from the one-shot duel clips (punch/hit/die), which
 * fire once on an action broadcast. The BODY render path treats these two specially: a
 * placeholder stub is **re-grounded to the stand pose** rather than dropped, so the war
 * stance stays visible (grounded, not floating) until the captain bakes real combat art -
 * see {@link ../render/playerArt.resolveBodyDuelClip}. The one-shot stubs are still
 * dropped (the corpse/ghost split, ADR-0140, and clothing-squares fix, ADR-0142, need
 * them absent).
 */
export const COMBAT_STANCE_CLIP_IDS: readonly string[] = ['combat-idle', 'combat-advance'];

/**
 * The people-action ids for the player **emotes** (ADR-0144): the `bow` (UO action 32)
 * and `salute` (action 33) social gestures a player fires by hotkey, plus the
 * {@link DANCE_EMOTE_ID dance} (action 30, ADR-0387) a player fires by typing the
 * classic `*dances*` action line. They are the wire clip names an
 * {@link ../net/protocol.ActionAnimationMessage} carries for an emote (an open string on
 * the wire, ADR-0116), so this list is the cross-slice contract the client hotkey, the
 * server broadcast, and the render degrade all key off. It is also the set the server's
 * {@link ../sim/commands.isEmoteId} validation gate derives from, so an id here is
 * player-triggerable by construction.
 *
 * Like the one-shot duel clips (punch/hit/die), a **placeholder** emote stub is dropped
 * to the stand pose on both the body and equipment render paths (never re-grounded like
 * the persistent combat stance) - see {@link ../render/playerArt.isPlaceholderEmoteClip}
 * - so an unbaked host degrades cleanly instead of floating a stub box or drawing a
 * clothing square. Adding another action-index emote is: an entry here + a matching
 * {@link actions} registry row + either a keybinding {@link ../input/keybindings.Action}
 * (bow / salute) or a chat trigger in `server/emote.ts` (the dance).
 */
export const EMOTE_CLIP_IDS: readonly string[] = ['bow', 'salute', 'dances'];

/**
 * The wire id of the **dance** emote (ADR-0387) - the third {@link EMOTE_CLIP_IDS}
 * member, triggered by either the `*dances*` chat line or its admin-only hotkey.
 * Named once here so the animation row, the server's say-line intercept, and the
 * repeat lookup below can never disagree about the string on the wire.
 */
export const DANCE_EMOTE_ID = 'dances';

/**
 * How many times the {@link DANCE_EMOTE_ID dance} clip plays through per trigger
 * (ADR-0387). A single pass of UO action 30 is over in well under a second - too brief
 * to read as dancing - so the broadcast carries this repeat count and the sprite loops
 * the clip that many times before returning to its stand pose. Ten is long enough for
 * a sustained dance while staying one gesture and one broadcast. The player is never
 * locked: movement or another action cancels it.
 */
export const DANCE_REPEAT = 10;

/**
 * How many times an emote's clip plays through on the broadcast (ADR-0387): the
 * {@link DANCE_REPEAT} loop for the dance, once for every other gesture. The single
 * lookup the server's emote path uses, so a repeating emote is declared here as data
 * rather than special-cased at the broadcast site.
 */
export function emoteRepeat(emote: string): number {
  return emote === DANCE_EMOTE_ID ? DANCE_REPEAT : 1;
}

/**
 * The people-action ids for one-shot **skill casts**: the arena runner's Spell1
 * volley (ADR-0187) and the dungeon Warrior's Spell2 Aegis buff (ADR-0413).
 * A one-shot with no persistent stance, so - exactly like the emote one-shots - a
 * **placeholder** cast stub is dropped to the stand pose on both the body and
 * equipment render paths (see {@link ../render/playerArt.isPlaceholderCastClip}).
 * Deliberately NOT in {@link EMOTE_CLIP_IDS}: that list also derives the server's
 * valid `Emote` command set ({@link ./commands.isEmoteId}); these casts are gameplay
 * actions rather than emotes.
 */
export const CAST_CLIP_IDS: readonly string[] = ['cast', 'spell2'];

/**
 * The people-action ids for the arena **archer's shots** (PRD #446 Slice 1,
 * ADR-0237): the bow shot (`shoot-bow`, UO action 18) and the Heavy Draw
 * crossbow (`shoot-crossbow`, action 19), fired on every `skill-fire` volley
 * for an archer run. Like {@link CAST_CLIP_IDS}, one-shots with no persistent
 * stance, so a **placeholder** stub is dropped to the stand pose on both the
 * body and equipment render paths (see {@link ../render/playerArt.isPlaceholderShootClip}).
 * Fired only by the arena sim's broadcast, never player-triggerable (deliberately
 * NOT in {@link EMOTE_CLIP_IDS}, which derives the valid `Emote` command set).
 */
export const SHOOT_CLIP_IDS: readonly string[] = ['shoot-bow', 'shoot-crossbow'];

/**
 * The people-action ids for **weapon swings** (The King's Justice guard's halberd
 * wack, `attack-2h` - PRD #361, ADR-0194). One-shots with no persistent stance, so
 * exactly like the emote/cast one-shots a **placeholder** stub is dropped to the
 * stand pose on both the body and equipment render paths (see
 * {@link ../render/playerArt.isPlaceholderWeaponClip}). Fired only by the event
 * sim's broadcast, never player-triggerable (deliberately NOT in
 * {@link EMOTE_CLIP_IDS}, which also derives the valid `Emote` command set).
 */
export const WEAPON_SWING_CLIP_IDS: readonly string[] = [
  'attack-2h',
  'thrust-2h',
  'thrust-1h',
];

/**
 * The equipment id of the **death shroud** a downed duelist wears (ClassicUO death
 * look, ADR-0142): a solid, grayscale, hooded-robe figure over the frozen die pose,
 * replacing their normal clothing.
 *
 * **This is the authentic UO death shroud, sourced from ClassicUO** (not a reuse of
 * the `robe.plain` cosmetic): the client's dead-shroud anim graphic is **`0x03CA`
 * (970)**, worn on the **Robe** layer - `PaperDollInteractable.GetAnimID` comments it
 * "graphic for dead shroud" and the gargoyle body remaps it to `0x0223`, confirming
 * `0x03CA` is the default human shroud. It is a single **hooded-robe** graphic, so it
 * carries both the robe and the hood in one anim (no separate hood layer needed). The
 * captain bakes anim 970 at `npm run convert` (they hold the UO art); britbank wires
 * the id here as a non-rollable `shroud.death` {@link equipment} entry and renders it
 * with no hue filter (grayscale) via {@link ../render/buildPlayerLayers}.
 */
export const SHROUD_ID = 'shroud.death';

/**
 * The **animal** (low-detail, bodies 200..399) actions baked for animal-class
 * bodies - today just the giant rat (ADR-0116). Animal bodies do NOT use the
 * human indices: in the low-detail table walk = 0, run = 1, **stand = 2** (not
 * 4), eat = 3, attacks = 5..7, **die = 8** (Die1), fidgets = 9/10 (ClassicUO
 * `LowAnimationGroup`). Only the clips the Rat Swarm needs are baked: walk +
 * stand for the wander, die for the stomp kill. VERIFY the indices against real
 * client data at bake time (clientless hosts run on stubs).
 */
export const animalActions = new Registry<ActionDefinition>('animal-action', [
  { id: 'walk', action: 0 },
  { id: 'stand', action: 2 },
  { id: 'die', action: 8 },
]);

/**
 * The bake set for a **mount** body (ADR-0442): the three gaits a ridden animal
 * needs and nothing else. Mounts are animal-band (200..399) bodies, so these are
 * the low-detail indices - walk 0, run 1, stand 2 - and NOT the people indices.
 *
 * Deliberately narrower than {@link animalActions}: a mount is never stomped, eaten,
 * or killed while ridden, so baking `die` would demand art the render path can never
 * reach. It DOES bake `run` (which the rat's set omits), because a mount is the only
 * body that gallops - the rider's {@link actions} `ride-fast` clip is meaningless
 * without a running mount underneath it.
 *
 * The three ids are exactly the locomotion names {@link ../render/playerSprite}'s
 * shared clock already selects by mode (gait -> walk/run, at rest -> stand), which is
 * why a mount needs no new render mode: it is just another layer whose clips happen
 * to come from a different body. See {@link ../render/buildPlayerLayers}.
 */
export const mountActions = new Registry<ActionDefinition>('mount-action', [
  { id: 'walk', action: 0 },
  { id: 'run', action: 1 },
  { id: 'stand', action: 2 },
]);

/** Mounted locomotion for high-detail monster bodies, which have no distinct run group. */
export const mountMonsterActions = new Registry<ActionDefinition>('mount-monster-action', [
  { id: 'walk', action: 0 },
  { id: 'run', action: 0 },
  { id: 'stand', action: 1 },
]);

/** One authored facing pose, reused for stand, walk, and run by the frame loader. */
export const singlePoseActions = new Registry<ActionDefinition>('single-pose-action', [
  { id: 'stand', action: 0 },
]);

/** Stand-only animal-layout bake used by card-only bestiary identities. */
export const cardAnimalActions = new Registry<ActionDefinition>('card-animal-action', [
  { id: 'stand', action: 2 },
]);

/** Stand-only monster-layout bake used by card-only bestiary identities. */
export const cardMonsterActions = new Registry<ActionDefinition>('card-monster-action', [
  { id: 'stand', action: 1 },
]);

/**
 * The bake set for the **ghost** bodies (PRD #252 Duel slice B,
 * ADR-0129): just the **stand** pose. Ghost bodies (0x192/0x193) sit in the
 * people index band, so `stand` is *hypothesised* to be the people index 4 (same
 * as a living body) - this is a restricted *subset* of {@link actions}, not a
 * different anim-group table. It is deliberately stand-only because a ghost has no
 * walk/combat/die clips in `anim.mul`. The dead player never moves (ghost movement
 * is out of PRD scope - a brief pre-resurrection visual, not a walkable afterlife),
 * so a single stand pose is all the render needs; the loader reuses it for walk/run
 * and holds it for fidget, exactly as the rat reuses its walk.
 *
 * **Confirmed vs ClassicUO (scout study, ADR-0142):** the captain's `npm run convert`
 * decodes ZERO frames for ghost body 0x192 - expected, not a bug, because a UO human
 * ghost has no animation art of its own (the client maps the ghost graphic to the
 * living body by subtracting 2). So the ghost bake is **non-fatal** ({@link
 * ../../tools/anim-converter/index.ts} skips a zero-frame ghost rather than aborting
 * the run), the ghost bodies stay registered as the replicated dead marker, and the
 * renderer draws a dead duelist off its correct-sex living body ({@link
 * ../render/playerArt.loadBodyFrames}, which carries the die clips) as the ClassicUO
 * death shroud - a solid, grayscale, robed figure over the frozen die pose ({@link
 * ../render/buildPlayerLayers}). This stand-only registry entry is therefore unused at
 * render time (kept as documentation of the people-band stand index 4).
 */
export const ghostActions = new Registry<ActionDefinition>('ghost-action', [
  { id: 'stand', action: 4 }, // people-band stand pose (CONFIRMED vs ClassicUO; unused at render).
]);

/**
 * The **monster** (high-detail, bodies < 200) actions - the codebase's first use of
 * the third UO anim-group table (Blackthorn's Storm Slice B, PRD #289, ADR-0148),
 * baked for the {@link ENERGY_VORTEX_BODY energy vortex}. Monster bodies do NOT use
 * the people or animal indices: in the high-detail table walk = 0, **stand = 1** (the
 * tool calls it Idle), **die = 2** (used for the end-of-event dissipate), the attacks
 * are Attack2 = 4 / AttackBow = 6 / AttackCrossbow = 7 (drawn at random per swing), and
 * **materialize = 12** (Stomp, played when a vortex is summoned into being).
 *
 * VERIFY every index against real client data at bake time (no UO client on dev/CI;
 * the stub pipeline covers those hosts, and the captain's `npm run convert` confirms).
 */
export const monsterActions = new Registry<ActionDefinition>('monster-action', [
  { id: 'walk', action: 0 }, // VERIFY
  { id: 'stand', action: 1 }, // Idle (VERIFY)
  { id: 'die', action: 2 }, // end-of-event dissipate (VERIFY)
  { id: 'attack-a', action: 4 }, // Attack2 (randomized swing) (VERIFY)
  { id: 'attack-b', action: 6 }, // AttackBow (VERIFY)
  { id: 'attack-c', action: 7 }, // AttackCrossbow (VERIFY)
  { id: 'materialize', action: 12 }, // Stomp - played on vortex summon (VERIFY)
]);

/**
 * The **bestiary** high-detail set (ADR-0184): the minimal monster-band action
 * group that *every* arena creature populates. The vortex's {@link monsterActions}
 * was VERIFY-shaped for the energy vortex's air-elemental base (graphic 13), which
 * is a *richer* body that also carries AttackCrossbow (7) and Stomp (12); the simple
 * bestiary creatures (slime, snake, orc, …) only populate attacks 4/5/6 (the standard
 * Attack1/2/3) and have no 7 and no 12, so decoding this whole set off the vortex table
 * aborts the fatal body bake on the first creature (slime, action 7). Confirmed against
 * the captain's client (`npm run convert` probe + UOFiddler): every registered creature
 * has 0/1/2/4/5/6, so this set bakes for all of them and keeps a wrong body id loud.
 *
 * The three `attack-a/b/c` clips the arena horde swings at random ({@link
 * ../sim/horde.HORDE_ATTACK_CLIPS}) map to Attack1/2/3 (4/5/6). There is no
 * `materialize` slot: arena creatures spawn through the portal, not a vortex summon,
 * so no body clip needs the Stomp pose the vortex plays on materialize.
 */
export const bestiaryActions = new Registry<ActionDefinition>('bestiary-action', [
  { id: 'walk', action: 0 }, // Walk
  { id: 'stand', action: 1 }, // Idle
  { id: 'die', action: 2 }, // Die1
  { id: 'attack-a', action: 4 }, // Attack1 (randomized swing)
  { id: 'attack-b', action: 5 }, // Attack2
  { id: 'attack-c', action: 6 }, // Attack3
]);

/**
 * A bestiary variant for high-detail bodies that carry only **two** attack clips
 * (Attack1/Attack2) rather than three - the arena boss's Demon Knight body (0x2E5 =
 * 741) is one: it has no Attack3 (action 6), so baking the plain {@link
 * bestiaryActions} throws on the missing clip. Here `attack-c` aliases Attack2 (action
 * 5) so every clip id the horde swings ({@link ../sim/horde.HORDE_ATTACK_CLIPS})
 * still bakes and resolves - the third random swing simply replays the second
 * animation. Identical to {@link bestiaryActions} otherwise.
 */
export const bestiaryTwoAttackActions = new Registry<ActionDefinition>('bestiary-two-attack-action', [
  { id: 'walk', action: 0 }, // Walk
  { id: 'stand', action: 1 }, // Idle
  { id: 'die', action: 2 }, // Die1
  { id: 'attack-a', action: 4 }, // Attack1 (randomized swing)
  { id: 'attack-b', action: 5 }, // Attack2
  { id: 'attack-c', action: 5 }, // no Attack3 on this body - alias Attack2
]);

/**
 * The restricted **people-band** action set for Lord Blackthorn (body 991,
 * Blackthorn's Storm Slice B, PRD #289, ADR-0148). Blackthorn is a people-band body
 * (400+, so his clips index the people strides) but bakes only walk / stand / a cast,
 * not the full player people table - baking a body clip is **fatal** on a miss, so his
 * plan must ask only for clips he has (the same reason the giant rat and the ghost use
 * their own restricted tables). walk = 0 and stand = 4 are the confirmed people indices;
 * `summon` is the cast clip at action index 17 (Spell2 per the captain's tool).
 *
 * VERIFY the Spell2 cast index (17): people-band cast indices deserve a confirming look
 * at real client data at the captain's `npm run convert`.
 */
export const blackthornActions = new Registry<ActionDefinition>('blackthorn-action', [
  { id: 'walk', action: 0 },
  { id: 'stand', action: 4 }, // people-band stand pose
  { id: 'summon', action: 17 }, // Spell2 cast, played on a vortex summon (VERIFY)
]);

/**
 * The bake set for the **chaos-llama** storm variant ({@link LLAMA_BODY}, 0xDC = 220,
 * ADR-0162). A llama reuses the entire {@link ../sim/vortex energy-vortex pursuit brain}
 * and lifecycle, so it broadcasts the *same clip ids a vortex does* - walk, stand, the
 * three `attack-a/b/c` swings, `materialize` on summon, and `die` on dissipate. But a
 * llama is an **animal**-band body (200..399), not the monster-band vortex, so those
 * same ids map to the low-detail animal anim-group indices (walk 0 / stand 2 / attacks
 * 5..7 / die 8 / fidget for the birth wiggle), NOT the {@link monsterActions} indices.
 * A restricted named set like {@link blackthornActions}: a body clip bakes **fatally** on
 * a miss, so the plan must ask only for clips the llama art actually has.
 *
 * VERIFY every index against real client data at the captain's `npm run convert` (no UO
 * client on dev/CI; the stub pipeline covers those hosts). `materialize` has no dedicated
 * low-group slot, so it reuses a fidget as the summon "wiggle into being" pose.
 */
export const llamaActions = new Registry<ActionDefinition>('llama-action', [
  { id: 'walk', action: 0 }, // VERIFY
  { id: 'run', action: 1 }, // low-detail animal run, used while mounted (VERIFY)
  { id: 'stand', action: 2 }, // low-detail animal stand (VERIFY)
  { id: 'attack-a', action: 5 }, // Attack1 (randomized swing) (VERIFY)
  { id: 'attack-b', action: 6 }, // Attack2 (VERIFY)
  { id: 'attack-c', action: 7 }, // Attack3 (VERIFY)
  { id: 'materialize', action: 10 }, // fidget reused as the summon wiggle (VERIFY)
  { id: 'die', action: 8 }, // Die1, end-of-event dissipate (VERIFY)
]);

/**
 * The bodies the converter bakes animation clips for: the splash-pickable player
 * bodies `male` (UO body 400) and `female` (401), plus the non-playable event/duel
 * creature bodies - the Rat Swarm `giant-rat` ({@link GIANT_RAT_BODY}, ADR-0116)
 * and the two duel `ghost` bodies ({@link GHOST_BODY_MALE}/{@link
 * GHOST_BODY_FEMALE}, ADR-0129) and the Blackthorn's Storm event bodies (the
 * monster-band {@link ENERGY_VORTEX_BODY energy vortex} and {@link BLACKTHORN_BODY
 * Lord Blackthorn}, ADR-0148). The bake set is, per body, that body's own action
 * table ({@link actionsForBody}) - people bodies bake the {@link actions} set
 * (attack + the duel combat clips), the rat bakes the {@link animalActions} set,
 * the ghosts bake the stand-only {@link ghostActions} set, the vortex bakes the
 * {@link monsterActions} set (decoded off its remapped base graphic, painted its
 * baked hue), Blackthorn bakes the restricted {@link blackthornActions} set, and the
 * chaos-llama vortex variant ({@link LLAMA_BODY}) bakes the animal-band {@link
 * llamaActions} set (its purple hue rides `renderable.bodyHue`, not a baked hue).
 */
export const bodies = new Registry<BodyDefinition>('body', [
  { id: 'male', body: 400 },
  { id: 'female', body: 401 },
  // The Rat Swarm giant rat: an animal-band body on the animal action table,
  // never offered at the splash / accepted at login (playable: false). Both the
  // id (0xD7 = 215, RunUO GiantRat) and the animal action indices are VERIFY -
  // confirmed against the captain's client data at bake time.
  { id: 'giant-rat', body: GIANT_RAT_BODY, actionSet: 'animal', playable: false },
  // The duel death ghosts (PRD #252 slice B, ADR-0129): the male/female ghost body
  // ids a defeated player is swapped onto - the replicated dead marker the renderer
  // keys the death-shroud look off. Non-playable like the rat (never offered at the
  // splash / accepted as a login `sex`), baked stand-only via the `ghost` set (a
  // zero-frame decode is now skipped, not fatal; ADR-0142). CONFIRMED vs ClassicUO:
  // these ids have no own art and map to the living body (subtract 2).
  { id: 'ghost-male', body: GHOST_BODY_MALE, actionSet: 'ghost', playable: false },
  { id: 'ghost-female', body: GHOST_BODY_FEMALE, actionSet: 'ghost', playable: false },
  // Blackthorn's Storm event bodies (PRD #289 Slice B, ADR-0148), both playable:false
  // (rejected at login like the giant rat). The energy vortex is the first MONSTER-band
  // body (< 200): it decodes off the air-elemental base graphic 13 (the 164->13 bodyconv
  // remap) painted hue 20, both recorded for the renderer. Lord Blackthorn is a people-
  // band human (991, no remap, hue 0) baking the restricted blackthorn set. Every id, the
  // remap, the hue, and the monster/cast indices are VERIFY at the captain's bake.
  {
    id: 'energy-vortex',
    body: ENERGY_VORTEX_BODY,
    actionSet: 'monster',
    playable: false,
    baseGraphic: ENERGY_VORTEX_BASE_GRAPHIC, // VERIFY: 164 -> 13 (air elemental) bodyconv remap
    hue: ENERGY_VORTEX_HUE, // VERIFY: hue 20
  },
  { id: 'blackthorn', body: BLACKTHORN_BODY, actionSet: 'blackthorn', playable: false },
  // The chaos-llama energy-vortex variant (ADR-0162): an animal-band body (0xDC = 220,
  // like the giant rat), never offered at the splash / accepted at login (playable:
  // false). No baseGraphic remap (the llama art sits plainly on body 220) and no baked
  // hue - the purple LLAMA_HUE rides renderable.bodyHue at spawn, like the vortex's own
  // hue. It reuses the vortex pursuit brain, so it bakes the vortex's clip ids mapped to
  // the animal table via the restricted `llama` set. Every index is VERIFY at the bake.
  {
    id: 'chaos-llama',
    body: LLAMA_BODY,
    actionSet: 'llama',
    mountable: true,
    playable: false,
  },
  {
    id: 'cat-mount',
    body: CAT_MOUNT_BODY,
    actionSet: 'mount',
    mountable: true,
    mountScale: 2,
    playable: false,
    baseGraphic: 0xd6,
    hue: 0x8fd,
    riderOffsetY: 0,
    riderIdleOffsetY: 18,
  },
  {
    id: 'mountain-goat-mount',
    body: MOUNTAIN_GOAT_MOUNT_BODY,
    actionSet: 'mount',
    mountable: true,
    mountScale: 2,
    playable: false,
    baseGraphic: 0xd1,
    riderOffsetY: 13,
    riderIdleOffsetY: 17,
  },
  {
    id: 'saint-bernard-mount',
    body: SAINT_BERNARD_MOUNT_BODY,
    actionSet: 'mount-monster',
    mountable: true,
    mountScale: 1.25,
    playable: false,
    riderOffsetY: 12,
    riderIdleOffsetY: 12,
  },
  {
    id: 'wolf-mount',
    body: WOLF_MOUNT_BODY,
    actionSet: 'mount-monster',
    mountable: true,
    mountScale: 1.25,
    playable: false,
    riderOffsetY: 11,
    riderIdleOffsetY: 10,
  },
  {
    id: 'poodle-mount',
    body: POODLE_MOUNT_BODY,
    actionSet: 'mount-monster',
    mountable: true,
    mountScale: 1.25,
    playable: false,
    riderOffsetY: 4,
    riderIdleOffsetY: 4,
  },
  {
    id: 'doberman-mount',
    body: DOBERMAN_MOUNT_BODY,
    actionSet: 'mount-monster',
    mountable: true,
    mountScale: 1.25,
    playable: false,
    riderOffsetY: 9,
    riderIdleOffsetY: 9,
  },
  {
    id: 'capybara-mount',
    body: CAPYBARA_MOUNT_BODY,
    actionSet: 'mount-monster',
    mountable: true,
    mountScale: 2,
    playable: false,
    riderOffsetY: 10,
    riderIdleOffsetY: 18,
  },
  {
    id: 'werewolf-mount',
    body: WEREWOLF_MOUNT_BODY,
    actionSet: 'mount-monster',
    mountable: true,
    mountScale: 1.5,
    playable: false,
    riderOffsetY: 20,
    riderIdleOffsetY: 25,
  },
  {
    id: 'lowrider',
    body: LOWRIDER_BODY,
    actionSet: 'single-pose',
    mountable: true,
    mountPresentation: 'replacement',
    playable: false,
    source: 'import',
  },
  // The camel mount (ADR-0442): a MOUNT-band body baking only the three ridden gaits
  // ({@link mountActions}), never offered at the splash / accepted at login like every
  // other creature body. `source: 'import'` because no UO client ships camel art - the
  // converter skips it and `npm run import-mobile` bakes its atlases from an external
  // sprite sheet instead. Registered here, in the one registry, so it stubs, streams,
  // and renders through exactly the same machinery as a decoded body.
  {
    id: 'camel',
    body: CAMEL_BODY,
    actionSet: 'mount',
    mountable: true,
    playable: false,
    source: 'import',
    riderOffsetY: CAMEL_RIDER_OFFSET_Y,
    riderIdleOffsetY: 40,
  },
  // The whole monster bestiary (ADR-0184): every creature registers as a
  // monster-band body on the high-detail monsterActions table, playable: false
  // (login-rejected). One spread keeps the bodies registry and the bestiary in
  // lockstep by construction - a new bestiary row bakes/stubs/login-gates with
  // zero further changes (the ADR-0148 mechanism). Card-only identities request
  // only an idle clip and remain outside world/Arena body recognition. Body ids
  // are VERIFY at the captain's bake; the two 200-band entries (darknight creeper
  // 313, demon knight 318) additionally VERIFY their bodyconv/expansion layout.
  ...((): BodyDefinition[] => {
    const registered = new Set<number>([
      GIANT_RAT_BODY,
      ENERGY_VORTEX_BODY,
      LLAMA_BODY,
      CAT_MOUNT_BODY,
      MOUNTAIN_GOAT_MOUNT_BODY,
      SAINT_BERNARD_MOUNT_BODY,
      WOLF_MOUNT_BODY,
      POODLE_MOUNT_BODY,
      DOBERMAN_MOUNT_BODY,
      CAPYBARA_MOUNT_BODY,
      WEREWOLF_MOUNT_BODY,
    ]);
    return monsters.all().flatMap((m) => {
      if (registered.has(m.body)) return [];
      registered.add(m.body);
      const actionSet: BodyActionSet = m.mountable
        ? 'mount-monster'
        : m.cardOnly
        ? (m.animationSet === 'animal' ? 'card-animal' : 'card-monster')
        : (m.id === 'demon-knight-boss' ? 'bestiary-two-attack' : 'bestiary');
      return [{
        id: m.id,
        body: m.body,
        actionSet,
        mountable: m.mountable,
        playable: false,
        baseGraphic: m.baseGraphic,
      }];
    });
  })(),
]);

/**
 * Every **living player** body id: the registry's playable subset, exactly the set
 * `server/identity.ts` derives its accepted login `sex` values from. Memoized once
 * (the {@link isMonsterBody MONSTER_BODIES} precedent) for the O(1) predicate below.
 */
const PLAYABLE_BODIES: ReadonlySet<number> = new Set(
  bodies.all().filter((b) => b.playable !== false).map((b) => b.body),
);

/**
 * The body an entity with **no** explicit `renderable.body` renders as - the male
 * player body, matching the renderer's own `DEFAULT_BODY` fallback. Read off the
 * registry rather than written as a literal, so it cannot drift from the row the
 * login gate and {@link PLAYABLE_BODIES} derive from.
 */
const DEFAULT_PLAYER_BODY = bodies.get('male').body;

/**
 * Whether a UO body id is a **living human** player body (today 400 male / 401 female)
 * - the only bodies that bake the people-band {@link actions} table, and so the only
 * ones carrying the player-emote clips ({@link EMOTE_CLIP_IDS}).
 *
 * The gate the server's emote path consults (ADR-0387): a rat, a vortex, Blackthorn, a
 * bestiary creature - and a **ghost** (the stand-only `ghost` action set) - have no
 * emote clip in their bake, so broadcasting one at them would name a clip nothing can
 * play. The emote is dropped there instead, keeping a bad clip off the wire. The
 * {@link isGhostBody}/{@link isStormBody} precedent: a body-id predicate read off the
 * already-replicated `renderable.body`, never a new snapshot field.
 *
 * An **absent** body reads as the {@link DEFAULT_PLAYER_BODY} - `renderable.body` is
 * optional and an entity without one renders as an ordinary human (the renderer's
 * default body), so the emote gate must agree with the render path rather than mute a
 * normal-looking player (a look rebuilt without an explicit body, e.g. by the portal
 * respawn, would otherwise be silently unable to bow/salute/dance).
 *
 * Derived from {@link bodies} rather than a hardcoded pair, so it stays in lockstep
 * with the login gate: registering another playable body makes it emote-capable and
 * loginable in one act, never loginable-but-mute.
 */
export function isHumanBody(body: number | undefined): boolean {
  return PLAYABLE_BODIES.has(body ?? DEFAULT_PLAYER_BODY);
}

/**
 * The action table a body's clips index into: its {@link BodyActionSet} selects
 * the per-class registry ({@link actions} for people, {@link animalActions} for
 * animals, {@link ghostActions} for the duel ghosts, {@link monsterActions} for the
 * high-detail energy vortex, {@link bestiaryActions} for the arena bestiary creatures,
 * {@link blackthornActions} for Lord Blackthorn, {@link
 * llamaActions} for the chaos-llama vortex variant). Both the converter and the
 * clientless stub derive each body's bake set through this one seam, so the baked
 * clips can never use the wrong table.
 */
export function actionsForBody(def: BodyDefinition): Registry<ActionDefinition> {
  if (def.actionSet === 'animal') return animalActions;
  if (def.actionSet === 'mount') return mountActions;
  if (def.actionSet === 'mount-monster') return mountMonsterActions;
  if (def.actionSet === 'single-pose') return singlePoseActions;
  if (def.actionSet === 'card-animal') return cardAnimalActions;
  if (def.actionSet === 'card-monster') return cardMonsterActions;
  if (def.actionSet === 'ghost') return ghostActions;
  if (def.actionSet === 'monster') return monsterActions;
  if (def.actionSet === 'bestiary') return bestiaryActions;
  if (def.actionSet === 'bestiary-two-attack') return bestiaryTwoAttackActions;
  if (def.actionSet === 'blackthorn') return blackthornActions;
  if (def.actionSet === 'llama') return llamaActions;
  return actions;
}

/**
 * The equippable cosmetics the game can layer over a body, and therefore the
 * anim graphics the converter bakes (each as `equipment × actions`, exactly as
 * `bodies × actions`). Hair (Slice 1) and beards (Slice 2, male-only
 * via `bodies: [400]`) are all **fully hued** (`partialHue` absent); the garment
 * set (Slice 3) plus the `robe`/`cloak` slots activated in Slice 4 (#57) are
 * **partial-hued** cloth dye (`partialHue: true`), which recolours only the
 * near-gray garment pixels and leaves authored coloured detail alone (see ADR-0032
 * and {@link ../render/hueFilter}). Slice 4 also grows the per-layer style lists;
 * every entry added there carries a VERIFY-flagged anim graphic pending the
 * captain-side `npm run convert` check (the clientless stub bakes placeholder
 * boxes until then, the existing behaviour).
 *
 * `anim` is the UO **anim graphic id** the converter feeds to `decodeAnimation` -
 * NOT the wearable item id. A worn item's animation lives in its `tiledata.mul`
 * Animation field: e.g. hair item 0x203b resolves to anim graphic 700, beard item
 * 0x2040 ("goatee") to 800, garment item 0x1539 ("long pants") to 431. Each entry
 * stores the resolved anim graphic; the inline comment records the source item id
 * it was resolved from. (Earlier slices held the item ids here directly, which
 * overflow `anim.mul` and decode to nothing - see the tiledata Animation field in
 * `tiledataMul.ts`.) Hair occupies anim graphics 700-712, beards 800-803.
 *
 * `bodies` declares which bodies an entry applies to: omitted means **both** (the
 * common case - one anim graphic shares both bodies, baked once); beards set
 * `bodies: [400]` so a 401 character never assembles one. A garment with a
 * distinct female cut is its own entry with its own `anim` and `bodies: [401]` -
 * no bake/render plumbing change, the registry already keys clips per id. Per-item
 * female variants are **VERIFY** against a client.
 *
 * Both the real converter and the placeholder stub iterate this same registry, so
 * the baked set can never drift.
 */
export const equipment = new Registry<EquipDefinition>('equipment', [
  { id: 'hair.short', layer: 'hair', anim: 700 }, // item 0x203b
  { id: 'hair.long', layer: 'hair', anim: 701 }, // item 0x203c "Long Hair"
  { id: 'hair.ponytail', layer: 'hair', anim: 702 }, // item 0x203d "Ponytail"
  { id: 'hair.mohawk', layer: 'hair', anim: 703 }, // item 0x2044
  { id: 'hair.pageboy', layer: 'hair', anim: 710 }, // item 0x2045
  // Captain-confirmed (#57 VERIFY walk): anim 712 is the *buns* graphic. The
  // entry that used to sit here as `hair.topknot` was mislabeled - there is no
  // topknot graphic at all, so that style is dropped. The earlier slice-4
  // `hair.buns` guess at anim 711 was also wrong; buns lives at 712.
  { id: 'hair.buns', layer: 'hair', anim: 712 }, // item 0x2046 (captain-confirmed: buns, not topknot)
  // Beards (Slice 2, #29): male-only facial hair, gated to body 400 via `bodies`
  // so a 401 character never assembles a beard layer. Fully hued like hair
  // (`partialHue` absent). Anim graphics 800-803, resolved from each beard item's
  // tiledata Animation field.
  { id: 'beard.short', layer: 'beard', anim: 802, bodies: [400] }, // item 0x203f "short beard"
  { id: 'beard.long', layer: 'beard', anim: 801, bodies: [400] }, // item 0x203e "long beard"
  { id: 'beard.goatee', layer: 'beard', anim: 800, bodies: [400] }, // item 0x2040 "goatee"
  { id: 'beard.mustache', layer: 'beard', anim: 803, bodies: [400] }, // item 0x2041 "mustache" (anim 803, captain-confirmed)
  // (Slice 4 (#57) tried a `beard.mutton` here at anim 804; the captain's walk
  // found the beard band ends at 803 - there is no mutton-chops graphic - so it
  // was dropped.)
  // Curated garment set (Slice 3): dyeable cloth, partial-hued. Anim graphics
  // resolved from each garment item's tiledata Animation field; shared across both
  // bodies (bake once) unless a distinct female cut is confirmed, in which case add
  // a `bodies:[401]` entry. `pants`/`skirt` are leg slots, `shirt`/`doublet` torso
  // slots, ordered by the facing-dependent table in render/layerOrder.ts.
  { id: 'pants.long', layer: 'pants', anim: 431, partialHue: true }, // item 0x1539 "long pants"
  { id: 'pants.short', layer: 'pants', anim: 430, partialHue: true }, // item 0x152e "short pants" (captain-confirmed: anim 430)
  { id: 'kilt.plain', layer: 'skirt', anim: 455, partialHue: true }, // item 0x1537 "kilt"
  { id: 'skirt.plain', layer: 'skirt', anim: 449, partialHue: true }, // item 0x1516 "skirt" (captain-confirmed: anim 449)
  { id: 'doublet.plain', layer: 'doublet', anim: 913, partialHue: true }, // item 0x1f7b "doublet"
  { id: 'shirt.fancy', layer: 'shirt', anim: 435, partialHue: true }, // item 0x1efd "fancy shirt"
  { id: 'shirt.plain', layer: 'shirt', anim: 434, partialHue: true }, // item 0x1517 "shirt" (captain-confirmed: anim 434, not 437)
  // The tunic: UOFiddler names anim 486 "formal shirt", but in-game it is called
  // just **Tunic** (captain's naming; the dotless id keeps itemLabel() at plain
  // "Tunic" rather than "Plain Tunic"). Graphic 486 has no frames of its own in
  // anim.mul - the client's Body.def remaps it (`486 {909} 0`), which the
  // converter now follows (tools/anim-converter/bodyDef.ts).
  { id: 'tunic', layer: 'shirt', anim: 486, partialHue: true }, // UOFiddler "formal shirt" (anim 486, Body.def -> 909)
  // (Slice 4 (#57) also tried `kilt.fancy` (0x153e) @457 here; no "fancy kilt" anim
  // exists in the captain's walk (455 kilt -> 456 Plate Mempo), so it stays dropped
  // - the skirt slot keeps one style.)
  // Slice 4 (#57): activate the dormant `robe`/`cloak` render slots. `EquipLayer`
  // and the layerOrder.ts draw table already cover both (so this is pure data, no
  // render plumbing), but the registry had zero entries and the splash no picker.
  // Partial-hued cloth like the garments. Anim graphics captain-confirmed against
  // the converter's anim browse (robe 0x1f03 -> 469, cloak 0x1515 -> 468); the
  // `robe.hooded` style was dropped (no confirmed hooded-robe graphic - the guess
  // at anim 921 turned out to be studded armor).
  { id: 'robe.plain', layer: 'robe', anim: 469, partialHue: true }, // item 0x1f03 "robe" (captain-confirmed: anim 469)
  { id: 'cloak.plain', layer: 'cloak', anim: 468, partialHue: true }, // item 0x1515 "cloak" (captain-confirmed: anim 468)
  // New garment layers (#57 follow-up): `dress`/`apron` join `EquipLayer` and the
  // layerOrder.ts draw table. Pure registry data + draw-order rows + splash slots,
  // no coerce/assemble/wire change. Every anim is captain-confirmed from the
  // converter's anim browse (the same walk that fixed robe/cloak). Partial-hued
  // cloth like the garments so authored colour survives dye. Ungated (both bodies) -
  // a player picks `None` for the slots a dress would otherwise overlap; there is no
  // layer suppression (a dress just draws over the shirt/legs, like a robe does).
  // The `apron` draws **topmost among garments** (over dress/robe), see ADR-0063.
  { id: 'dress.plain', layer: 'dress', anim: 447, partialHue: true }, // "plain dress" (captain-confirmed: anim 447)
  { id: 'dress.fancy', layer: 'dress', anim: 448, partialHue: true }, // "fancy dress" (captain-confirmed: anim 448)
  { id: 'apron.full', layer: 'apron', anim: 465, partialHue: true }, // "full apron" (captain-confirmed: anim 465)
  { id: 'apron.half', layer: 'apron', anim: 466, partialHue: true }, // "half apron" (captain-confirmed: anim 466)
  // Headgear (`headgear` layer, ADR-0063): the head/face band - the face masks
  // (bear 414 .. skullcap 419) and the hat band (floppy 404 .. jester 413, plus the
  // bandana at 492) **share one render slot**, collapsed from the former separate
  // `hat`/`mask` layers per the captain's intent so a worn hat and a worn mask
  // occupy the same slot. One head slot: a hat, a mask and a bandana never stack
  // (first requested layer wins). Most are partial-hued cloth/straw/fur so authored
  // colour survives dye; ungated (both bodies).
  { id: 'mask.bear', layer: 'headgear', anim: 414 }, // "bear mask" (captain-confirmed: anim 414)
  { id: 'mask.deer', layer: 'headgear', anim: 415, partialHue: true }, // "deer mask" (captain-confirmed: anim 415)
  { id: 'mask.orc', layer: 'headgear', anim: 416 }, // "orc mask" (captain-confirmed: anim 416)
  { id: 'mask.tribal', layer: 'headgear', anim: 417, partialHue: true }, // "tribal mask" (captain-confirmed: anim 417; a 2nd tribal variant sits at 418, omitted to keep the picker clean)
  { id: 'mask.skullcap', layer: 'headgear', anim: 419, partialHue: true }, // "skullcap" (captain-confirmed: anim 419)
  { id: 'hat.floppy', layer: 'headgear', anim: 404, partialHue: true }, // "floppy hat" (captain-supplied: anim 404)
  { id: 'hat.widebrim', layer: 'headgear', anim: 405, partialHue: true }, // "wide-brim hat" (anim 405)
  { id: 'hat.cap', layer: 'headgear', anim: 406, partialHue: true }, // "cap" (anim 406)
  { id: 'hat.tallstraw', layer: 'headgear', anim: 407, partialHue: true }, // "tall straw hat" (anim 407)
  { id: 'hat.straw', layer: 'headgear', anim: 408, partialHue: true }, // "straw hat" (anim 408)
  { id: 'hat.wizard', layer: 'headgear', anim: 409, partialHue: true }, // "wizard's hat" (anim 409)
  { id: 'hat.bonnet', layer: 'headgear', anim: 410, partialHue: true }, // "bonnet" (anim 410)
  { id: 'hat.feathered', layer: 'headgear', anim: 411, partialHue: true }, // "feathered hat" (anim 411)
  { id: 'hat.tricorne', layer: 'headgear', anim: 412, partialHue: true }, // "tricorne hat" (anim 412)
  { id: 'hat.jester', layer: 'headgear', anim: 413, partialHue: true }, // "jester hat" (anim 413)
  { id: 'hat.bandana', layer: 'headgear', anim: 492, partialHue: true }, // "bandana" head-wrap (anim 492, shares the head slot)
  // The kasa: the wide conical straw hat from UO's Samurai Empire band (captain's
  // browse: anim 516, real frames of its own). Dotless id so itemLabel() reads
  // plain "Kasa" - "Kasa Hat" would be redundant. Shares the one head slot.
  { id: 'kasa', layer: 'headgear', anim: 516 }, // "kasa" (anim 516)
  // The bone mask: worn face art cut from the "bone helmet" graphic (captain's
  // browse: anim 562, real frames of its own), joining the mask band on the same
  // head slot.
  { id: 'mask.bone', layer: 'headgear', anim: 562 }, // UOFiddler "bone helmet" (anim 562)
  // Admin-only novelty headgear: ServUO's Bascinet uses item 0x140C. Keep the
  // tiledata-backed item id here so the converter resolves the wearable anim
  // graphic from the real client instead of guessing it. The renderer doubles
  // this layer alone; `rollable:false` keeps it out of progression/wardrobe.
  {
    id: 'bascinet.giant',
    layer: 'headgear',
    itemGraphic: 0x140c,
    visualScale: 2,
    rollable: false,
  },
  // Body sash (`sash` layer): a torso accessory worn *over* the shirt/doublet, so
  // it gets its own draw slot rather than replacing the base garment. Partial-hued
  // cloth. Captain-supplied anim 490 from the same browse. (The "Leather Haidate"
  // at 491 sits between sash and bandana in that browse but is leg armour, not
  // requested, so it stays out of the registry for now.)
  { id: 'sash.body', layer: 'sash', anim: 490, partialHue: true }, // "body sash" (anim 490)
  // Footwear (`feet` layer): the captain-supplied boot/shoe band - thigh boots 476,
  // plain boots 477, sandals 479, shoes 480, fur boots 482. (The "Plate Hiro Sode"
  // at 478 between them is arm armour, not footwear, so it stays out.) A new
  // `EquipLayer` slot + a layerOrder.ts draw row low in the stack: over the body's
  // feet, under the leg garments so pants/skirts cover the boot tops. Partial-hued
  // dyeable leather/cloth. One foot slot: boots, sandals and shoes never stack.
  { id: 'boots.thigh', layer: 'feet', anim: 476, partialHue: true }, // "thigh boots" (anim 476)
  { id: 'boots.plain', layer: 'feet', anim: 477, partialHue: true }, // "boots" (anim 477)
  { id: 'sandals.plain', layer: 'feet', anim: 479 }, // "sandals" (anim 479)
  { id: 'shoes.plain', layer: 'feet', anim: 480, partialHue: true }, // "shoes" (anim 480)
  { id: 'boots.fur', layer: 'feet', anim: 482, partialHue: true }, // "fur boots" (anim 482)
  // Held/worn accessories (`accessory` layer, ADR-0063): the single shared slot for
  // hand-held and worn accessories - the captain-supplied lantern 500, torch 501 and
  // candle 502 (the held light sources), plus any future accessory of this kind. All
  // consolidated onto one `accessory` slot (renamed from the former `light`-only
  // slot) + a layerOrder.ts draw row high in the stack (over the garments, in the
  // hand). One accessory slot: a torch and a lantern never stack.
  { id: 'lantern.lit', layer: 'accessory', anim: 500 }, // "lantern" (anim 500)
  { id: 'torch.lit', layer: 'accessory', anim: 501 }, // "torch" (anim 501)
  { id: 'candle.lit', layer: 'accessory', anim: 502 }, // "candle" (anim 502)
  // The UO **death shroud** (`robe` layer): a SYSTEM layer the game puts on a downed
  // duelist, not a player-earnable cosmetic (`rollable: false` - never rolled,
  // unlocked, or shown in the wardrobe; see SHROUD_ID). Anim graphic 0x03CA = 970 is
  // ClassicUO's dead-shroud graphic (a single hooded robe - robe + hood in one), baked
  // by the captain at `npm run convert` like any other anim id. Rendered grayscale (no
  // hue filter) by buildPlayerLayers, so `partialHue` is moot and omitted.
  { id: 'shroud.death', layer: 'robe', anim: 0x03ca, rollable: false }, // UO dead shroud (ClassicUO 0x03CA=970)
  // The King's Justice guard kit (PRD #361, ADR-0194): the first armor/weapon
  // layers - platemail chest/arms/legs and the halberd. SYSTEM layers the event
  // assembles onto the guard (`rollable: false` - never rolled, unlocked, or shown
  // in the wardrobe; the shroud precedent). Fully hued metal (no `partialHue`; the
  // guard wears them unhued, so they render the raw grayscale plate). Anim graphics
  // resolved from a real client's tiledata.mul Animation field (item 0x1415
  // "platemail" -> 527, 0x1410 "platemail arms" -> 528, 0x141A "platemail legs" ->
  // 529, 0x143E "halberd" -> 624); VERIFY at the captain's `npm run convert` bake.
  { id: 'plate.chest', layer: 'armor-chest', anim: 527, rollable: false }, // "platemail" (item 0x1415)
  { id: 'plate.arms', layer: 'armor-arms', anim: 528, rollable: false }, // "platemail arms" (item 0x1410)
  { id: 'plate.legs', layer: 'armor-legs', anim: 529, rollable: false }, // "platemail legs" (item 0x141A)
  { id: 'halberd', layer: 'weapon', anim: 624, rollable: false }, // "halberd" (item 0x143E)
  { id: 'sword.britannian', layer: 'weapon', itemGraphic: 0x0f5e, rollable: false },
  // The Arena archer's held weapons (PRD #446 Slice 1, ADR-0237); also the duelist's
  // "Fire your bow!" weapon (ADR-0250 / this ADR). SYSTEM `weapon`-layer items (the
  // halberd precedent, `rollable: false` - never rolled/worn in town), equipped
  // render-only onto the run/duel entity. Always visible (an archer holds the bow),
  // animating its own `shoot-bow`/`shoot-crossbow` clip on a volley. The anim graphic
  // is **resolved from tiledata** by `itemGraphic` (the item's `Animation` field), so
  // the wrong-guess bug that made the archer hold a spear is gone - no placeholder to
  // drift. A wrong *item id* would still need a bake eyeball, but 0x13B2/0x0F50 are
  // the documented bow/crossbow statics.
  { id: 'bow', layer: 'weapon', itemGraphic: 0x13b2, rollable: false }, // "bow"
  { id: 'crossbow', layer: 'weapon', itemGraphic: 0x0f50, rollable: false }, // "crossbow"
]);

/** All registries in one bag, for callers that want the whole content set. */
export const content = {
  hues,
  tiles,
  statics,
  bloodArt,
  cursors,
  sounds,
  music,
  actions,
  bodies,
  equipment,
} as const;

/**
 * The {@link BodyDefinition} for a mount body id, or undefined when the id is not a
 * registered mount. The lookup the render path uses to read a mount's own rider lift,
 * scale, and fixed hue without hardcoding any particular animal.
 */
export function mountBody(body: number | undefined): BodyDefinition | undefined {
  if (body === undefined || !isMountBody(body)) return undefined;
  return bodies.all().find((b) => b.body === body);
}
